import React, { useState, useEffect, useRef } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { Icon } from "@iconify/react";
import { createEventRegistration, getEventsByRole, getEventById, createUser, createAccountContact, sendEmailOtp, verifyEmailOtp, checkUserExists } from "../../../Utils/apiServices";
import { stripNumbersFromName } from "../../../Utils/nameValidation";
import { useAuth } from "../../../Context/authContext";
import TextField from "../../../Components/TextField";
import PhoneVerify from "../../../Components/PhoneVerify";
import Button from "../../../Components/Button";
import { Typography } from "../../../Components/Typography";
import GeminiAutocomplete from "../../../Components/GeminiAutocomplete";
import mentorSignUpImage from "../../../assets/Mentor_SignUp.png";

// Partner-like roles: Ecosystem Partner & Corporate Sponsor (same flow as legacy partner)
const isPartnerLikeRole = (r) => r === "ecosystem_partner" || r === "corporate_sponsor";
const isPartnerOrInvestorRole = (r) => isPartnerLikeRole(r) || r === "investor";
const roleDisplayName = (r) => ({ ecosystem_partner: "Ecosystem Partner", corporate_sponsor: "Corporate Sponsor" }[r] || (r ? String(r).replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()) : ""));

/** Accept domain-only input (e.g. microsoft.com); return normalized http(s) URL or null. */
const normalizeWebsiteUrlInput = (raw) => {
  const trimmed = (raw || "").trim();
  if (!trimmed) return null;
  const tryParse = (value) => {
    try {
      const u = new URL(value);
      if (u.protocol !== "http:" && u.protocol !== "https:") return null;
      if (!u.hostname) return null;
      return u.href;
    } catch {
      return null;
    }
  };
  return tryParse(trimmed) || tryParse(`https://${trimmed}`);
};

/** Investor signup step 1 — sent on account create as `account.investment_profile` when API supports it. */
const INVESTMENT_PROFILE_OPTIONS = [
  { value: "angel_investor", label: "Angel Investor", hint: "Individual deploying personal capital" },
  { value: "venture_capital", label: "Venture Capital (VC)", hint: "Institutional fund managing LP capital" },
  { value: "family_office", label: "Family Office", hint: "Private wealth management entity" },
  { value: "cvc", label: "Corporate Venture Capital (CVC)", hint: "" },
  { value: "private_equity", label: "Private Equity (PE)", hint: "Growth stage / Buyout focus" },
  { value: "impact_investor", label: "Impact Investor", hint: "Focused on social/environmental ROI" },
  { value: "incubator_accelerator", label: "Incubator / Accelerator", hint: "Early-stage support and funding" },
];

// Initial formData structures for each role
const getVolunteerFormData = () => ({
  event_id: "",
  email: "",
  first_name: "",
  last_name: "",
  phone: "",
  country_code: "US",
  current_occupation: "",
  organization_employer: "",
  job_title_major: "",
  linkedin_profile: "",
  years_of_experience: "",
  primary_expertise: [],
  preferred_role: [],
  technical_stack: [],
  weekly_availability: "",
  timezone: "",
  commitment_length: "",
  communication_preference: [],
  motivation_statement: "",
  previous_volunteering: "",
  cv_resume: null,
  code_of_conduct: false,
  volunteer_role_acknowledgement: false,
  activities_to_volunteer_for: [],
  availability_and_motivation: "",
});

const getMentorFormData = () => ({
  email: "",
  username: "",
  first_name: "",
  last_name: "",
  password: "",
  re_enter_password: "",
  phone_number: "",
  country_code: "US",
});

const getStudentFormData = () => ({
  email: "",
  username: "",
  first_name: "",
  last_name: "",
  password: "",
  re_enter_password: "",
  phone_number: "",
  country_code: "US",
});

const getPartnerInvestorFormData = () => ({
  // Step 1 fields
  sponsor_name: "",
  legal_entity_name: "",
  website_url: "",
  investment_profile: "", // investor only
  funding_amount: "", // partner only: number for account.funding_amount
  // Step 2 fields (primary contact)
  first_name: "",
  last_name: "",
  username: "",
  password: "",
  re_enter_password: "",
  contact_role: "",
  contact_email: "",
  contact_phone: "",
  contact_country_code: "US",
  funding_configuration: "", // legacy; partner uses funding_amount for API
  // Secondary contact (contact_2)
  contact_2_first_name: "",
  contact_2_last_name: "",
  contact_2_username: "",
  contact_2_password: "",
  contact_2_contact_role: "",
  contact_2_email: "",
  contact_2_phone: "",
  contact_2_country_code: "US",
});

// Unregistered event participant (role=user from URL): event + email + participation type, then about you
const getUnregisteredUserFormData = () => ({
  event_id: "",
  email: "",
  participation_type: "", // mentor_advisor | founder_innovator_student | investor_angel | sponsor_brand_partner | ecosystem_community_partner
  first_name: "",
  last_name: "",
  phone: "",
  country_code: "US",
  primary_expertise: [],
  preferred_roles: [],
});

const PARTICIPATION_TYPE_OPTIONS = [
  { value: "mentor_advisor", label: "Mentor / Advisor" },
  { value: "founder_innovator_student", label: "Founder / Innovator / Student" },
  { value: "investor_angel", label: "Investor / Angel" },
  { value: "sponsor_brand_partner", label: "Sponsor / Brand Partner" },
  { value: "ecosystem_community_partner", label: "Ecosystem / Community Partner" },
];

const PARTICIPATION_TYPE_TO_API_ROLE = {
  mentor_advisor: "unregistered_mentor",
  founder_innovator_student: "unregistered_founder",
  investor_angel: "unregistered_investor",
  sponsor_brand_partner: "unregistered_sponsor",
  ecosystem_community_partner: "unregistered_ecosystem",
};

const PRIMARY_EXPERTISE_OPTIONS = [
  "Artificial Intelligence / Technology",
  "Product & Innovation",
  "Venture Capital / Investment",
  "Business Strategy",
  "Corporate Leadership",
  "Marketing & Growth",
  "Legal / Governance",
  "Academia / Research",
  "Operations",
  "Social Impact",
  "Policy & Public Sector",
  "Other",
];

const INTENT_INTEREST_OPTIONS = [
  "Mentor emerging talent",
  "Discover investment opportunities",
  "Explore partnerships",
  "Brand visibility",
  "Learning & insights",
  "Talent acquisition",
  "Ecosystem building",
  "Community engagement",
  "Other",
];

const SignUp = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [searchParams] = useSearchParams();
  const [currentStep, setCurrentStep] = useState(0);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [mentorStage, setMentorStage] = useState("form"); // form | otp | thanks
  const [volunteerStage, setVolunteerStage] = useState("form"); // form | thanks
  const [mentorOtpCode, setMentorOtpCode] = useState("");
  const [mentorOtpEmail, setMentorOtpEmail] = useState("");
  const [partnerInvestorStage, setPartnerInvestorStage] = useState("form"); // form | otp
  const [showSecondaryContact, setShowSecondaryContact] = useState(false);
  const [partnerInvestorOtpCode, setPartnerInvestorOtpCode] = useState("");
  const [partnerInvestorOtpEmail, setPartnerInvestorOtpEmail] = useState("");
  // Phone ownership verification (Twilio Verify SMS OTP). Only one phone field is
  // ever mounted at a time (the role branches are mutually exclusive), so a single
  // pair covers every flow. The verified number is kept rather than a bare boolean:
  // PhoneVerify re-reports on mount, so a boolean would be cleared by any remount,
  // and keying on the number self-invalidates as soon as it is edited.
  const [verifiedPhoneE164, setVerifiedPhoneE164] = useState("");
  const [currentPhoneE164, setCurrentPhoneE164] = useState("");
  const phoneVerified = Boolean(currentPhoneE164) && currentPhoneE164 === verifiedPhoneE164;

  const handlePhoneVerifiedChange = (isVerified, e164) => {
    setCurrentPhoneE164(e164 || "");
    if (isVerified) {
      setVerifiedPhoneE164(e164 || "");
      setError("");
    }
  };
  const [events, setEvents] = useState([]);
  const [loadingEvents, setLoadingEvents] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [showCommunicationConsent, setShowCommunicationConsent] = useState(false);
  const [communicationConsentPending, setCommunicationConsentPending] = useState(false);
  const [showCodeOfConduct, setShowCodeOfConduct] = useState(false);
  const [hasViewedCodeOfConduct, setHasViewedCodeOfConduct] = useState(false);
  const [passwordFieldError, setPasswordFieldError] = useState("");
  const [reEnterPasswordFieldError, setReEnterPasswordFieldError] = useState("");
  const [emailExistsError, setEmailExistsError] = useState("");
  const [usernameExistsError, setUsernameExistsError] = useState("");
  const latestEmailRef = useRef("");
  const latestUsernameRef = useRef("");
  const MAX_MULTISELECT = 3;

  const communicationConsentText =
    "By selecting this, I verify this is my phone number and I consent to receive automated and pre-recorded/artificial calls, including text messages, relating to my relationship with IgniteNext. I understand these calls or text messages may relate to the handling, servicing, and billing for any IgniteNext relationship I may have, and may be recurring texts. Message and Data rates may apply. You will be sent a confirmation message to your mobile device.";

  const codeOfConductPopupContent =
    `Our Commitment: IgniteNext is dedicated to providing a safe, inclusive, and professional environment for student innovators, volunteers, and mentors. We believe that innovation thrives in an atmosphere of mutual respect and integrity.

1. Professional Conduct & Integrity
Mentorship First: Volunteers and Mentors agree to prioritize the student's educational and entrepreneurial growth.
No Solicitation: Mentors and Volunteers may not solicit student founders for equity, fees, or employment during their active participation in the program without explicit written consent from IgniteNext.
Accuracy: Volunteers shall provide honest and accurate feedback based on their professional expertise.

2. Inclusive Environment: We have a zero-tolerance policy for harassment or discrimination based on race, gender, sexual orientation, disability, physical appearance, or religion.
• Use welcoming and inclusive language.
• Be respectful of differing viewpoints and experiences.
• Gracefully accept constructive criticism.

3. Confidentiality & IP
Respect the Spark: Volunteers may be exposed to sensitive "Sparks" (ideas) and intellectual property. All participants agree to treat student ideas with strict confidentiality.
Non-Appropriation: Volunteers will not use, share, or claim ownership of any ideas or IP developed by students on the platform.

4. Boundaries & Safety
Professional Channels: All communications should ideally take place through official IgniteNext channels or professional platforms (e.g., Google meet, professional email).
Reporting: If you witness or are subjected to unacceptable behavior, please report it immediately to Admin@ignitenext.org.

5. Consequences: IgniteNext reserves the right to remove any participant (student, volunteer, or mentor) who violates this Code of Conduct, without prior notice.

By participating in IgniteNext, you agree to uphold these values and help us build a world-class innovation ecosystem.`;
  
  // Funding tier options for Corporate Sponsor (dropdown values must stay in sync with API / account.funding_amount)
  const FUNDING_TIER_OPTIONS = [
    { value: "5000", label: "Bronze: Ecosystem Supporter - $5,000", config: "Bronze $5000" },
    { value: "10000", label: "Silver: Talent Discovery - $10,000", config: "Silver $10000" },
    { value: "15000", label: "Gold: Market Intelligence - $15,000", config: "Gold $15000" },
    { value: "25000", label: "Platinum: Ecosystem Governance - $25,000", config: "Platinum $25000" },
  ];
  const mapFundingParamToAmount = (param) => {
    const p = (param || "").toString().trim().toLowerCase();
    const m = { bronze: "5000", silver: "10000", gold: "15000", platinum: "25000" };
    return m[p] || "";
  };
  const mapFundingToConfiguration = (funding) => {
    const fundingMap = {
      bronze: "Bronze $5000",
      silver: "Silver $10000",
      gold: "Gold $15000",
      platinum: "Platinum $25000"
    };
    return fundingMap[funding?.toLowerCase()] || "";
  };

  // If URL has a valid role (?role=mentor|volunteer|student|ecosystem_partner|corporate_sponsor|investor), lock the role and hide the dropdown.
  const roleParam = (searchParams.get("role") || "").toLowerCase();
  const roleFromUrl = roleParam === "mentor" || roleParam === "volunteer" || roleParam === "student" || roleParam === "ecosystem_partner" || roleParam === "corporate_sponsor" || roleParam === "investor" ? roleParam : null;

  // Get URL parameters for defaults (trim for reliable matching)
  const eventIdFromUrl = searchParams.get("eventId") || searchParams.get("event_id") || "";
  const fundingFromUrl = (searchParams.get("funding") || "").trim().toLowerCase();

  const [isRoleLocked, setIsRoleLocked] = useState(Boolean(roleFromUrl));
  const [selectedRole, setSelectedRole] = useState(roleFromUrl || "student");
  const [formData, setFormData] = useState(() => {
    if (roleFromUrl === "mentor" || roleFromUrl === "student") {
      return roleFromUrl === "mentor" ? getMentorFormData() : getStudentFormData();
    }
    if (isPartnerOrInvestorRole(roleFromUrl)) {
      const partnerData = getPartnerInvestorFormData();
      if (fundingFromUrl) {
        partnerData.funding_configuration = mapFundingToConfiguration(fundingFromUrl);
        if (roleFromUrl === "corporate_sponsor") {
          partnerData.funding_amount = mapFundingParamToAmount(fundingFromUrl) || partnerData.funding_amount;
        }
      }
      return partnerData;
    }
    if (roleFromUrl === "user") {
      const userData = getUnregisteredUserFormData();
      if (eventIdFromUrl) userData.event_id = eventIdFromUrl;
      return userData;
    }
    const volunteerData = getVolunteerFormData();
    if (eventIdFromUrl) {
      volunteerData.event_id = eventIdFromUrl;
    }
    return volunteerData;
  });
  const [userExistsMessage, setUserExistsMessage] = useState("");
  const [userStage, setUserStage] = useState("form"); // form | thanks (for role=user flow)
  const [userEventName, setUserEventName] = useState(""); // event title when role=user & eventId in URL

  // If the URL role changes, apply it.
  useEffect(() => {
    const nextRoleParam = (searchParams.get("role") || "").toLowerCase();
    const nextRoleFromUrl =
      nextRoleParam === "mentor" || nextRoleParam === "volunteer" || nextRoleParam === "student" || nextRoleParam === "ecosystem_partner" || nextRoleParam === "corporate_sponsor" || nextRoleParam === "investor" ? nextRoleParam : null;

    const nextEventIdFromUrl = searchParams.get("eventId") || searchParams.get("event_id") || "";
    const nextFundingFromUrl = (searchParams.get("funding") || "").trim().toLowerCase();

    setIsRoleLocked(Boolean(nextRoleFromUrl));
    if (nextRoleFromUrl) {
      setSelectedRole(nextRoleFromUrl);
      if (isPartnerOrInvestorRole(nextRoleFromUrl)) {
        const partnerData = getPartnerInvestorFormData();
        if (nextFundingFromUrl) {
          partnerData.funding_configuration = mapFundingToConfiguration(nextFundingFromUrl);
          if (nextRoleFromUrl === "corporate_sponsor") {
            partnerData.funding_amount = mapFundingParamToAmount(nextFundingFromUrl) || partnerData.funding_amount;
          }
        }
        setFormData(partnerData);
        setCurrentStep(0);
      } else if (nextRoleFromUrl === "volunteer") {
        const volunteerData = getVolunteerFormData();
        if (nextEventIdFromUrl) {
          volunteerData.event_id = nextEventIdFromUrl;
        }
        setFormData(volunteerData);
      } else if (nextRoleFromUrl === "user") {
        const userData = getUnregisteredUserFormData();
        if (nextEventIdFromUrl) userData.event_id = nextEventIdFromUrl;
        setFormData(userData);
        setUserExistsMessage("");
        setUserStage("form");
      }
    }
  }, [searchParams]);

  // Sync funding dropdown from URL when role is corporate_sponsor (e.g. direct load with ?role=corporate_sponsor&funding=bronze)
  useEffect(() => {
    const fundingParam = (searchParams.get("funding") || "").trim().toLowerCase();
    if (!fundingParam) return;
    const amount = mapFundingParamToAmount(fundingParam);
    if (!amount) return;
    setFormData((prev) => {
      if (prev.funding_amount === amount) return prev;
      if (selectedRole !== "corporate_sponsor") return prev;
      return { ...prev, funding_amount: amount, funding_configuration: mapFundingToConfiguration(fundingParam) };
    });
  }, [searchParams, selectedRole]);

  // Preemptive password validation (mentor/student): show errors as user types, before submit
  const PASSWORD_REQUIREMENT_MSG =
    "Password must be at least 8 characters and include uppercase, lowercase, number, and a special character (e.g. #)";
  const getPasswordRequirementError = (value) => {
    if (!value?.trim()) return "";
    const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z\d])\S{8,}$/;
    return passwordRegex.test(value) ? "" : PASSWORD_REQUIREMENT_MSG;
  };
  useEffect(() => {
    if (selectedRole !== "mentor" && selectedRole !== "student" && !isPartnerOrInvestorRole(selectedRole)) {
      setPasswordFieldError("");
      setReEnterPasswordFieldError("");
      return;
    }
    setPasswordFieldError(getPasswordRequirementError(formData.password));
    const reEnter = formData.re_enter_password;
    if (!reEnter?.trim()) {
      setReEnterPasswordFieldError("");
    } else {
      const requirementErr = getPasswordRequirementError(reEnter);
      setReEnterPasswordFieldError(
        requirementErr || (reEnter !== formData.password ? "Passwords do not match" : "")
      );
    }
  }, [selectedRole, formData.password, formData.re_enter_password]);

  // Dynamic steps based on role
  const totalSteps = selectedRole === "volunteer" ? 6 : (isPartnerOrInvestorRole(selectedRole) ? 2 : 1); // Volunteer: 6 steps, Partner/Investor: 2 steps, Mentor/Student: 1 step

  // Step titles based on role
  const volunteerStepTitles = [
    "Account Information",
    "Identity",
    "Skills & Role",
    "Commitment",
    "Motivation",
    "Compliance"
  ];

  const unregisteredUserStepTitles = [
    "Choose Your Role",
    "Tell Us About You"
  ];

  const mentorStepTitles = [
    "Account Information"
  ];

  const partnerInvestorStepTitles =
    selectedRole === "corporate_sponsor"
      ? ["Establishing Your Brand Presence", "Just a few more details"]
      : ["Let's get started with the basics", "Just a few more details"];

  const stepTitles = selectedRole === "volunteer" 
    ? volunteerStepTitles 
    : (isPartnerOrInvestorRole(selectedRole) ? partnerInvestorStepTitles : mentorStepTitles);

  useEffect(() => {
    const fetchEvents = async () => {
      setLoadingEvents(true);
      try {
        // For role=user use same event list as volunteer
        const roleForApi = selectedRole === "user" ? "volunteer" : selectedRole;
        const response = await getEventsByRole(roleForApi);
        if (response.success && response?.data?.data) {
          setEvents(
            Array.isArray(response.data?.data)
              ? response.data.data
              : []
          );
        } else {
          setError("Failed to load events. Please refresh the page.");
        }
      } catch (err) {
        setError("Failed to load events. Please refresh the page.");
      } finally {
        setLoadingEvents(false);
      }
    };

    if (selectedRole === "volunteer" || selectedRole === "user") {
      fetchEvents();
    }
  }, [selectedRole]);

  // When role=user and eventId in URL, fetch event name for title
  useEffect(() => {
    const eventIdFromUrl = searchParams.get("eventId") || searchParams.get("event_id") || "";
    if (selectedRole === "user" && eventIdFromUrl) {
      getEventById(eventIdFromUrl)
        .then((res) => {
          if (res?.success && res?.data?.data?.title) {
            setUserEventName(res.data.data.title);
          }
        })
        .catch(() => setUserEventName(""));
    } else {
      setUserEventName("");
    }
  }, [selectedRole, searchParams]);

  // Reset formData and step when role changes
  useEffect(() => {
    if (selectedRole === "volunteer") {
      const volunteerData = getVolunteerFormData();
      const urlEventId = searchParams.get("eventId") || searchParams.get("event_id") || "";
      if (urlEventId) volunteerData.event_id = urlEventId;
      setFormData(volunteerData);
    } else if (selectedRole === "user") {
      const userData = getUnregisteredUserFormData();
      const urlEventId = searchParams.get("eventId") || searchParams.get("event_id") || "";
      if (urlEventId) userData.event_id = urlEventId;
      setFormData(userData);
      setUserExistsMessage("");
      setUserStage("form");
    } else if (selectedRole === "mentor") {
      setFormData(getMentorFormData());
    } else if (selectedRole === "student") {
      setFormData(getStudentFormData());
    } else if (isPartnerOrInvestorRole(selectedRole)) {
      const partnerData = getPartnerInvestorFormData();
      const fundingParam = (searchParams.get("funding") || "").trim().toLowerCase();
      if (selectedRole === "corporate_sponsor" && fundingParam) {
        const amount = mapFundingParamToAmount(fundingParam);
        if (amount) {
          partnerData.funding_amount = amount;
          partnerData.funding_configuration = mapFundingToConfiguration(fundingParam);
        }
      }
      setFormData(partnerData);
      setPartnerInvestorStage("form");
      setPartnerInvestorOtpCode("");
      setPartnerInvestorOtpEmail("");
    }
    setCurrentStep(0);
    setError("");
    setEmailExistsError("");
    setUsernameExistsError("");
    setMentorStage("form");
    setVolunteerStage("form");
    setMentorOtpCode("");
    setMentorOtpEmail("");
    setHasViewedCodeOfConduct(false);
  }, [selectedRole, searchParams]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownOpen && !event.target.closest('.dropdown-container')) {
        setDropdownOpen(false);
      }
    };

    if (dropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [dropdownOpen]);

  const handleInputChange = (field, value) => {
    if (field === "first_name" || field === "last_name") {
      value = stripNumbersFromName(value);
    }
    setFormData((prev) => ({ ...prev, [field]: value }));
    setError(""); // Clear error on input change
    if (field === "email") {
      latestEmailRef.current = value;
      setEmailExistsError("");
      setUserExistsMessage("");
    }
    if (field === "username") {
      latestUsernameRef.current = value;
      setUsernameExistsError("");
    }
    if (field === "contact_email") {
      setEmailExistsError("");
    }
  };

  const handleEmailBlur = () => {
    const valueToCheck = formData.email?.trim?.() || "";
    if (!valueToCheck) {
      setEmailExistsError("");
      return;
    }
    checkUserExists(valueToCheck)
      .then((res) => {
        if (latestEmailRef.current !== valueToCheck) return;
        const exists = res?.data?.data?.exists;
        const field = res?.data?.data?.field;
        if (exists && field === "email") {
          setEmailExistsError("This email is already registered.");
        } else {
          setEmailExistsError("");
        }
      })
      .catch(() => {
        if (latestEmailRef.current === valueToCheck) setEmailExistsError("");
      });
  };

  const handleUsernameBlur = () => {
    const valueToCheck = (formData.username?.trim?.() || "").toLowerCase();
    if (!valueToCheck) {
      setUsernameExistsError("");
      return;
    }
    latestUsernameRef.current = valueToCheck;
    checkUserExists(valueToCheck)
      .then((res) => {
        if (latestUsernameRef.current !== valueToCheck) return;
        const exists = res?.data?.data?.exists;
        const field = res?.data?.data?.field;
        if (exists && field === "username") {
          setUsernameExistsError("This username is already taken.");
        } else {
          setUsernameExistsError("");
        }
      })
      .catch(() => {
        if (latestUsernameRef.current === valueToCheck) setUsernameExistsError("");
      });
  };

  // Contact email uniqueness check for ecosystem_partner / corporate_sponsor / investor (step 2)
  const handleContactEmailBlur = () => {
    const valueToCheck = (formData.contact_email?.trim?.() || "").toLowerCase();
    if (!valueToCheck) {
      setEmailExistsError("");
      return;
    }
    latestEmailRef.current = valueToCheck;
    checkUserExists(valueToCheck)
      .then((res) => {
        if (latestEmailRef.current !== valueToCheck) return;
        const exists = res?.data?.data?.exists;
        const field = res?.data?.data?.field;
        if (exists && field === "email") {
          setEmailExistsError("This email is already registered.");
        } else {
          setEmailExistsError("");
        }
      })
      .catch(() => {
        if (latestEmailRef.current === valueToCheck) setEmailExistsError("");
      });
  };

  const handleLimitedMultiSelect = (field, selectedValues, label) => {
    if (selectedValues.length <= MAX_MULTISELECT) {
      handleInputChange(field, selectedValues);
      return;
    }
    setError(`You can select up to ${MAX_MULTISELECT} for ${label}.`);
  };

  const acceptCommunicationConsent = () => {
    if (!communicationConsentPending) return;
    const currentPreferences = formData.communication_preference || [];
    if (!currentPreferences.includes("SMS/WhatsApp")) {
      handleInputChange("communication_preference", [...currentPreferences, "SMS/WhatsApp"]);
    }
    setCommunicationConsentPending(false);
    setShowCommunicationConsent(false);
  };

  const cancelCommunicationConsent = () => {
    setCommunicationConsentPending(false);
    setShowCommunicationConsent(false);
  };

  const validateStep = (step) => {
    setError("");
    
    // Partner/Investor validation (Ecosystem Partner & Corporate Sponsor require funding amount)
    if (isPartnerOrInvestorRole(selectedRole)) {
      switch (step) {
        case 0: // Step 1: Basics
          if (!formData.sponsor_name?.trim()) {
            setError(selectedRole === "investor" ? "Investment Entity is required" : "Company Name is required");
            return false;
          }
          if (!formData.legal_entity_name?.trim()) {
            setError(
              selectedRole === "investor"
                ? "Registered Fund Name is required"
                : selectedRole === "ecosystem_partner"
                  ? "Registered Corporate Entity is required"
                  : "Legal Entity Name is required"
            );
            return false;
          }
          if (!formData.website_url?.trim()) {
            setError(
              selectedRole === "investor" ? "Portfolio / Firm URL is required" : "Corporate Portal URL is required"
            );
            return false;
          }
          if (!normalizeWebsiteUrlInput(formData.website_url)) {
            setError(
              selectedRole === "investor"
                ? "Please enter a valid portfolio / firm URL"
                : "Please enter a valid corporate portal URL"
            );
            return false;
          }
          if (selectedRole === "investor" && !(formData.investment_profile || "").trim()) {
            setError("Investment Profile is required");
            return false;
          }
          if (selectedRole === "corporate_sponsor") {
            const amount = formData.funding_amount;
            if (amount === "" || amount === undefined || amount === null) {
              setError("Please select a partnership tier");
              return false;
            }
            const num = Number(amount);
            if (Number.isNaN(num) || num < 0) {
              setError("Please select a valid partnership tier");
              return false;
            }
          }
          return true;
        
        case 1: // Step 2: Contact Details
          if (!formData.first_name?.trim()) {
            setError("First name is required");
            return false;
          }
          if (!formData.last_name?.trim()) {
            setError("Last name is required");
            return false;
          }
          const partnerUsername = (formData.username?.trim?.() || "").toLowerCase();
          if (!partnerUsername) {
            setError("Username is required");
            return false;
          }
          if (partnerUsername.length < 3) {
            setError("Username must be at least 3 characters");
            return false;
          }
          if (partnerUsername.length > 30) {
            setError("Username must be 30 characters or less");
            return false;
          }
          const usernameRegex = /^(?=.{3,30}$)[A-Za-z0-9](?:[A-Za-z0-9._-]*[A-Za-z0-9])?$/;
          if (!usernameRegex.test(partnerUsername)) {
            setError(
              "Username can only use letters, numbers, periods (.), underscores (_), and hyphens (-), and must start and end with a letter or number"
            );
            return false;
          }
          if (!formData.password?.trim()) {
            setError("Password is required");
            return false;
          }
          const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z\d])\S{8,}$/;
          if (!passwordRegex.test(formData.password)) {
            setError(
              "Password must be at least 8 characters and include uppercase, lowercase, number, and a special character (e.g. #)"
            );
            return false;
          }
          if (!(formData.re_enter_password || "").trim()) {
            setError("Verify Password is required");
            return false;
          }
          if (formData.password !== formData.re_enter_password) {
            setError("Passwords do not match");
            return false;
          }
          if (reEnterPasswordFieldError) {
            return false;
          }
          if (!formData.contact_role?.trim()) {
            setError("Contact Role is required");
            return false;
          }
          if (!formData.contact_email?.trim()) {
            setError("Contact Email is required");
            return false;
          }
          // Basic email validation
          const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
          if (!emailRegex.test(formData.contact_email)) {
            setError("Please enter a valid email address");
            return false;
          }
          if (!formData.contact_phone?.trim()) {
            setError("Direct Line is required");
            return false;
          }
          if (!phoneVerified) {
            setError('Please verify the Direct Line — use "Verify with OTP" under the phone field.');
            return false;
          }
          if (showSecondaryContact) {
            const c2First = (formData.contact_2_first_name || "").trim();
            const c2Last = (formData.contact_2_last_name || "").trim();
            const c2Username = (formData.contact_2_username || "").trim().toLowerCase();
            const c2Password = formData.contact_2_password;
            const c2Role = (formData.contact_2_contact_role || "").trim();
            const c2Email = (formData.contact_2_email || "").trim().toLowerCase();
            const c2PhoneRaw = (formData.contact_2_phone || "").replace(/\D/g, "");
            if (!c2First || !c2Last || !c2Username || !c2Password || !c2Role || !c2Email || !c2PhoneRaw) {
              setError("When adding a secondary contact, all secondary contact fields are required.");
              return false;
            }
          }
          // Block submit if username or contact email is already taken
          if (usernameExistsError) return false;
          if (emailExistsError) return false;
          return true;
        
        default:
          return true;
      }
    }
    
    if (selectedRole === "mentor" || selectedRole === "student") {
      // Mentor validation (single step)
      const email = formData.email?.trim?.() || "";
      const username = formData.username?.trim?.() || "";

      if (!email) {
        setError("Email is required");
        return false;
      }
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email)) {
        setError("Please enter a valid email address");
        return false;
      }
      if (!username) {
        setError("Username is required");
        return false;
      }
      if (username.length < 3) {
        setError("Username must be at least 3 characters");
        return false;
      }
      if (username.length > 30) {
        setError("Username must be 30 characters or less");
        return false;
      }
      // Username validation: 3–30 chars; letters, numbers, ., _, -; must start/end with alphanumeric
      const usernameRegex = /^(?=.{3,30}$)[A-Za-z0-9](?:[A-Za-z0-9._-]*[A-Za-z0-9])?$/;
      if (!usernameRegex.test(username)) {
        setError(
          "Username can only use letters, numbers, periods (.), underscores (_), and hyphens (-), and must start and end with a letter or number"
        );
        return false;
      }
      if (!formData.first_name || !formData.last_name) {
        setError("First name and last name are required");
        return false;
      }
      if (formData.first_name.trim().length < 2) {
        setError("First name must be at least 2 characters");
        return false;
      }
      if (formData.last_name.trim().length < 2) {
        setError("Last name must be at least 2 characters");
        return false;
      }
      if (!formData.password) {
        setError("Password is required");
        return false;
      }
      // Password validation: at least 8 characters, 1 uppercase, 1 lowercase, 1 number, 1 special character (e.g. #)
      // Disallow whitespace to avoid accidental copy/paste issues.
      const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z\d])\S{8,}$/;
      if (!passwordRegex.test(formData.password)) {
        setError(
          "Password must be at least 8 characters and include uppercase, lowercase, number, and a special character (e.g. #)"
        );
        return false;
      }
      if (formData.password !== formData.re_enter_password) {
        setError("Passwords do not match");
        return false;
      }
      if (!formData.phone_number) {
        setError("Phone number is required");
        return false;
      }
      // Phone number validation: should be numeric and at least 10 digits
      const phoneRegex = /^\d{10,15}$/;
      if (!phoneRegex.test(formData.phone_number.replace(/\s+/g, ''))) {
        setError("Phone number must be 10-15 digits");
        return false;
      }
      if (!phoneVerified) {
        setError('Please verify your phone number — use "Verify with OTP" under the phone field.');
        return false;
      }
      // Block submit if email or username is already taken (from async check)
      if (emailExistsError) return false;
      if (usernameExistsError) return false;
      return true;
    }

    if (selectedRole === "user") {
      // Unregistered user (event participant) validation
      switch (step) {
        case 0: // Choose Your Role: event, email, participation type
          if (!formData.event_id) {
            setError("Please select an event");
            return false;
          }
          if (!formData.email?.trim()) {
            setError("Email is required");
            return false;
          }
          const userEmailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
          if (!userEmailRegex.test(formData.email.trim())) {
            setError("Please enter a valid email address");
            return false;
          }
          if (!formData.participation_type) {
            setError("Please select how you would like to participate");
            return false;
          }
          return true;
        case 1: // Tell Us About You
          if (!formData.first_name?.trim()) {
            setError("Full name (first name) is required");
            return false;
          }
          if (!formData.last_name?.trim()) {
            setError("Full name (last name) is required");
            return false;
          }
          if (!formData.phone?.trim()) {
            setError("Phone number is required");
            return false;
          }
          const phoneDigits = (formData.phone || "").replace(/\D/g, "");
          if (phoneDigits.length < 10 || phoneDigits.length > 15) {
            setError("Phone number must be 10–15 digits");
            return false;
          }
          if (!phoneVerified) {
            setError('Please verify your phone number — use "Verify with OTP" under the phone field.');
            return false;
          }
          if (!formData.primary_expertise?.length) {
            setError("Please select at least one primary expertise (max 3)");
            return false;
          }
          if (formData.primary_expertise.length > MAX_MULTISELECT) {
            setError(`Primary expertise: select up to ${MAX_MULTISELECT} options`);
            return false;
          }
          if (!formData.preferred_roles?.length) {
            setError("Please select at least one intent or interest (max 3)");
            return false;
          }
          if (formData.preferred_roles.length > MAX_MULTISELECT) {
            setError(`Intent & interest: select up to ${MAX_MULTISELECT} options`);
            return false;
          }
          return true;
        default:
          return true;
      }
    }

    // Volunteer validation (multi-step)
    if (selectedRole === "volunteer") {
      switch (step) {
        case 0: // Account Info
          if (!formData.event_id) {
            setError("Event ID is required");
            return false;
          }
          if (!formData.email) {
            setError("Email is required");
            return false;
          }
          const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
          if (!emailRegex.test(formData.email)) {
            setError("Please enter a valid email address");
            return false;
          }
          return true;
        
        case 1: // Identity
          if (!formData.first_name || !formData.last_name) {
            setError("First name and last name are required");
            return false;
          }
          if (!formData.current_occupation) {
            setError("Current occupation is required");
            return false;
          }
          if (!formData.years_of_experience) {
            setError("Years of Experience is required");
            return false;
          }
          if (formData.phone) {
            // Phone number validation: should be numeric and at least 10 digits
            const phoneRegex = /^\d{10,15}$/;
            if (!phoneRegex.test(formData.phone.replace(/\s+/g, ''))) {
              setError("Phone number must be 10-15 digits");
              return false;
            }
            // Phone is optional for volunteers, so this only bites once one is given.
            if (!phoneVerified) {
              setError('Please verify your phone number — use "Verify with OTP" under the phone field.');
              return false;
            }
          }
          return true;
        
        case 2: // Skills & Role
          if (formData.primary_expertise.length === 0) {
            setError("Please select at least one primary expertise");
            return false;
          }
          if (formData.preferred_role.length === 0) {
            setError("Please select at least one preferred role");
            return false;
          }
          return true;
        
        case 3: // Commitment
          if (!formData.weekly_availability) {
            setError("Weekly Availability is required");
            return false;
          }
          if (!formData.timezone) {
            setError("Timezone is required");
            return false;
          }
          if (!formData.commitment_length) {
            setError("Commitment Length is required");
            return false;
          }
          if (!formData.communication_preference || formData.communication_preference.length === 0) {
            setError("At least one Communication Preference is required");
            return false;
          }
          return true;
        
        case 4: // Motivation
          if (!formData.motivation_statement) {
            setError("Motivation Statement is required");
            return false;
          }
          const charCount = formData?.motivation_statement?.trim()?.length;
          if (charCount > 150) {
            setError("Motivation Statement must be 150 characters or less");
            return false;
          }
          const prevVolLen = (formData.previous_volunteering || "").trim().length;
          if (prevVolLen > 150) {
            setError("Previous Volunteering must be 150 characters or less");
            return false;
          }
          return true;
        
        case 5: // Compliance
          if (!formData.code_of_conduct) {
            setError("You must agree to the Code of Conduct");
            return false;
          }
          if (!formData.volunteer_role_acknowledgement) {
            setError("You must acknowledge this is a volunteer role");
            return false;
          }
          return true;
        
        default:
          return true;
      }
    }
  };

  const handleNext = () => {
    if (!validateStep(currentStep)) return;
    // Re-entering step 2 (contact) from step 1 must show the form, not a stale OTP screen (see partner Back -> Next flow)
    if (isPartnerOrInvestorRole(selectedRole) && currentStep === 0) {
      setPartnerInvestorStage("form");
      setPartnerInvestorOtpCode("");
    }
    setCurrentStep((prev) => Math.min(prev + 1, totalSteps - 1));
  };

  // For role=user step 0: validate then check if email exists; if exists show login message, else go to step 1
  const handleUserStep0Next = async () => {
    if (!validateStep(0)) return;
    setError("");
    setUserExistsMessage("");
    setSubmitting(true);
    try {
      const res = await checkUserExists(formData.email?.trim() || "");
      const exists = res?.data?.data?.exists && res?.data?.data?.field === "email";
      if (exists) {
        setUserExistsMessage("You're already part of IgniteNext. Please log in with your password and register for this event under the Events section in the portal.");
        setSubmitting(false);
        return;
      }
      setCurrentStep(1);
    } catch (err) {
      setError("Unable to verify email. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleBack = () => {
    setError("");
    if (isPartnerOrInvestorRole(selectedRole) && currentStep === 1 && partnerInvestorStage === "otp") {
      setPartnerInvestorStage("form");
      setPartnerInvestorOtpCode("");
      return;
    }
    // Leaving contact step for company step: clear OTP sub-state so Next returns to the form, not verification
    if (isPartnerOrInvestorRole(selectedRole) && currentStep === 1) {
      setPartnerInvestorStage("form");
      setPartnerInvestorOtpCode("");
    }
    setCurrentStep((prev) => Math.max(prev - 1, 0));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    try {
      let dataToSend;
      let response;

      if (selectedRole === "mentor" || selectedRole === "student") {
        const email = formData.email?.trim?.() || "";

        // Step 1: Send OTP after mentor/student form is filled
        if (mentorStage === "form") {
          if (!validateStep(currentStep)) return;

          setSubmitting(true);
          const sendRes = await sendEmailOtp({ email });
          const otpSent = sendRes?.success && sendRes?.data?.success !== false;

          if (!otpSent) {
            setError(sendRes?.data?.message || sendRes?.error || "Unable to send OTP. Please try again.");
            setSubmitting(false);
            return;
          }

          setMentorOtpEmail(email);
          setMentorStage("otp");
          setMentorOtpCode("");
          setSubmitting(false);
          return;
        }

        // Step 2: Verify OTP then create mentor/student
        if (mentorStage === "otp") {
          const code = (mentorOtpCode || "").trim();
          if (!/^\d{6}$/.test(code)) {
            setError("Please enter the 6-digit verification code");
            return;
          }

          setSubmitting(true);
          const verifyRes = await verifyEmailOtp({ email: mentorOtpEmail || email, otp_code: code });
          const verified = verifyRes?.success && verifyRes?.data?.success === true;

          if (!verified) {
            setError(verifyRes?.data?.message || verifyRes?.error || "OTP verification failed. Please try again.");
            setSubmitting(false);
            return;
          }

          // Mentor/Student: send phone with country code prefix in same field
          const mentorCountryCodeToPrefix = { US: "+1", CA: "+1", UK: "+44", GB: "+44", AU: "+61", IN: "+91" };
          const mentorCode = formData.country_code || "US";
          const mentorPrefix = mentorCountryCodeToPrefix[mentorCode] || "+1";
          const mentorPhoneDigits = (formData.phone_number || "").replace(/\D/g, "");
          // Prefer the number that was actually OTP-verified. The prefix map above
          // only covers 6 countries and falls back to "+1", so it would otherwise
          // store a different number than the one we verified.
          const mentorPhoneWithPrefix = currentPhoneE164 || mentorPrefix + mentorPhoneDigits;

          dataToSend = {
            username: formData.username.trim().toLowerCase(),
            email: email,
            password: formData.password,
            first_name: formData.first_name.trim(),
            last_name: formData.last_name.trim(),
            phone: mentorPhoneWithPrefix,
            role: selectedRole === "student" ? "student" : "mentor",
          };
          response = await createUser(dataToSend);

          if (response.success) {
            // Auto-login with the same email and password, then redirect to role-specific page
            const loginResult = await login(dataToSend.email, dataToSend.password);
            setSubmitting(false);
            if (loginResult?.success) {
              const userRole = loginResult.user?.role?.toLowerCase?.()?.trim?.();
              if (userRole === "mentor") {
                navigate("/mentor-dashboard");
              } else {
                navigate("/");
              }
              return;
            }
            // If login failed, show thanks screen so user can log in manually
            setMentorStage("thanks");
            return;
          }

          setError(response.error || "Unable to sign up. Please try again.");
          setSubmitting(false);
          return;
        }

        return;
      } else if (isPartnerOrInvestorRole(selectedRole)) {
        // If on last step and still in form stage: send OTP first
        if (currentStep === totalSteps - 1 && partnerInvestorStage === "form") {
          if (!validateStep(currentStep)) {
            return;
          }
          const email = formData.contact_email?.trim() || "";
          setSubmitting(true);
          const sendRes = await sendEmailOtp({ email });
          const otpSent = sendRes?.success && sendRes?.data?.success !== false;
          if (!otpSent) {
            setError(sendRes?.data?.message || sendRes?.error || "Unable to send OTP. Please try again.");
            setSubmitting(false);
            return;
          }
          setPartnerInvestorOtpEmail(email);
          setPartnerInvestorStage("otp");
          setPartnerInvestorOtpCode("");
          setError("");
          setSubmitting(false);
          return;
        }

        // If in OTP stage: verify then create account-contact
        if (partnerInvestorStage === "otp") {
          const code = (partnerInvestorOtpCode || "").trim();
          if (!/^\d{6}$/.test(code)) {
            setError("Please enter the 6-digit verification code");
            return;
          }
          setSubmitting(true);
          const verifyRes = await verifyEmailOtp({
            email: partnerInvestorOtpEmail || formData.contact_email?.trim() || "",
            otp_code: code,
          });
          const verified = verifyRes?.success && verifyRes?.data?.success === true;
          if (!verified) {
            setError(verifyRes?.data?.message || verifyRes?.error || "OTP verification failed. Please try again.");
            setSubmitting(false);
            return;
          }
          // Build payload and call createAccountContact
          const countryCodeToPrefix = { US: "+1", CA: "+1", UK: "+44", GB: "+44", AU: "+61", IN: "+91" };
          const codePrefix = formData.contact_country_code || "US";
          const prefix = countryCodeToPrefix[codePrefix] || "+1";
          const phoneDigits = (formData.contact_phone || "").replace(/\D/g, "");
          // Prefer the OTP-verified number over the 6-country prefix map.
          const phone = currentPhoneE164 || prefix + phoneDigits;

          const accountPayload = {
            name: formData.sponsor_name.trim(),
            legal_entity_name: formData.legal_entity_name.trim(),
            website: normalizeWebsiteUrlInput(formData.website_url),
          };
          if (selectedRole === "investor" && (formData.investment_profile || "").trim()) {
            accountPayload.investment_profile = String(formData.investment_profile).trim();
          }
          if (selectedRole === "corporate_sponsor" && formData.funding_amount !== "" && formData.funding_amount != null) {
            const amount = Number(formData.funding_amount);
            if (!Number.isNaN(amount) && amount >= 0) {
              accountPayload.funding_amount = amount;
            }
          }

          const accountContactPayload = {
            account: accountPayload,
            contact: {
              email: formData.contact_email.trim(),
              username: formData.username.trim().toLowerCase(),
              role: selectedRole,
              password: formData.password,
              company_role: formData.contact_role?.trim() || "",
              first_name: formData.first_name.trim(),
              last_name: formData.last_name.trim(),
              phone,
            },
          };
          if (showSecondaryContact) {
            const c2First = (formData.contact_2_first_name || "").trim();
            const c2Last = (formData.contact_2_last_name || "").trim();
            const c2Username = (formData.contact_2_username || "").trim().toLowerCase();
            const c2Password = formData.contact_2_password;
            const c2Role = (formData.contact_2_contact_role || "").trim();
            const c2Email = (formData.contact_2_email || "").trim().toLowerCase();
            const c2Prefix = countryCodeToPrefix[formData.contact_2_country_code || "US"] || "+1";
            const c2PhoneDigits = (formData.contact_2_phone || "").replace(/\D/g, "");
            const c2Phone = c2Prefix + c2PhoneDigits;
            accountContactPayload.contact_2 = {
              email: c2Email,
              username: c2Username,
              role: selectedRole,
              password: c2Password,
              company_role: c2Role,
              first_name: c2First,
              last_name: c2Last,
              phone: c2Phone,
            };
          }

          response = await createAccountContact(accountContactPayload);
          if (response.success) {
            const username = formData.username.trim().toLowerCase();
            const password = formData.password;
            const loginResult = await login(username, password);
            setSubmitting(false);
            if (loginResult?.success) {
              navigate("/complete-registration");
              return;
            }
            navigate("/login", { state: { message: `Thank you! Your ${roleDisplayName(selectedRole) || selectedRole} registration has been submitted successfully. Please sign in to continue.` } });
            return;
          }
          setError(response.error || "Unable to sign up. Please try again.");
          setSubmitting(false);
          return;
        }

        if (!validateStep(currentStep)) {
          return;
        }
        // If not on last step, go to next step
        if (currentStep < totalSteps - 1) {
          if (currentStep === 0) {
            setPartnerInvestorStage("form");
            setPartnerInvestorOtpCode("");
          }
          setCurrentStep(currentStep + 1);
          setSubmitting(false);
          return;
        }
      } else if (selectedRole === "user") {
        // Unregistered event participant: submit via same event-registration API with unregistered_* role
        if (!validateStep(currentStep)) return;
        setSubmitting(true);
        const userCountryCodeToPrefix = { US: "+1", CA: "+1", UK: "+44", GB: "+44", AU: "+61", IN: "+91" };
        const userCode = formData.country_code || "US";
        const userPrefix = userCountryCodeToPrefix[userCode] || "+1";
        const userPhoneDigits = (formData.phone || "").replace(/\D/g, "");
        // Prefer the OTP-verified number over the 6-country prefix map.
        const userPhoneWithPrefix = userPhoneDigits
          ? currentPhoneE164 || userPrefix + userPhoneDigits
          : undefined;
        const apiRole = PARTICIPATION_TYPE_TO_API_ROLE[formData.participation_type] || "unregistered_founder";
        dataToSend = {
          event_id: parseInt(formData.event_id),
          email: formData.email?.trim?.() || "",
          name: `${formData?.first_name || ""} ${formData?.last_name || ""}`.trim(),
          phone: userPhoneWithPrefix,
          country_code: formData.country_code || "US",
          primary_expertise: formData.primary_expertise || [],
          preferred_roles: formData.preferred_roles || [],
          role: apiRole,
          status: "registered",
        };
        response = await createEventRegistration(dataToSend);
        if (response.success) {
          setSubmitting(false);
          setUserStage("thanks");
          return;
        }
        setError(response.error || "Unable to submit. Please try again.");
        setSubmitting(false);
        return;
      } else {
        if (!validateStep(currentStep)) {
          return;
        }
        setSubmitting(true);

        // Volunteer registration: send phone with country code prefix in same field
        const volunteerCountryCodeToPrefix = { US: "+1", CA: "+1", UK: "+44", GB: "+44", AU: "+61", IN: "+91" };
        const volunteerCode = formData.country_code || "US";
        const volunteerPrefix = volunteerCountryCodeToPrefix[volunteerCode] || "+1";
        const volunteerPhoneDigits = (formData.phone || "").replace(/\D/g, "");
        // Prefer the OTP-verified number over the 6-country prefix map.
        const volunteerPhoneWithPrefix = volunteerPhoneDigits
          ? currentPhoneE164 || volunteerPrefix + volunteerPhoneDigits
          : undefined;

        dataToSend = {
          event_id: parseInt(formData.event_id),
          name: `${formData?.first_name} ${formData?.last_name}`?.trim(),
          current_occupation: formData.current_occupation,
          organization_employer: formData.organization_employer,
          job_title_or_major: formData.job_title_major,
          linkedin_profile: formData.linkedin_profile,
          years_of_experience: formData.years_of_experience
            ? parseInt(formData.years_of_experience)
            : undefined,
          email: formData.email,
          phone: volunteerPhoneWithPrefix,
          country_code: formData.country_code || "US",
          primary_expertise: formData.primary_expertise,
          preferred_roles: formData.preferred_role,
          technical_stack: Array.isArray(formData.technical_stack) ? formData.technical_stack.join(",") : (formData.technical_stack || ""),
          role: "volunteer",
          weekly_availability: formData.weekly_availability,
          timezone: formData.timezone,
          commitment_length: formData.commitment_length,
          communication_preference: formData.communication_preference,
          motivation_statement: formData.motivation_statement,
          previous_volunteering: formData.previous_volunteering,
          code_of_conduct_accepted: formData.code_of_conduct,
          status: "registered",
          admin_review_notes: "Pending review",
          total_hours_contributed: 0,
          certificate_issued: false,
        };
        response = await createEventRegistration(dataToSend);
      }

      if (response?.success) {
        setSubmitting(false);
        if (selectedRole === "volunteer") {
          setVolunteerStage("thanks");
        } else if (isPartnerOrInvestorRole(selectedRole)) {
          // Show success message and navigate to login
          navigate("/login", { state: { message: `Thank you! Your ${roleDisplayName(selectedRole) || selectedRole} registration has been submitted successfully.` } });
        } else {
          navigate("/login");
        }
      } else {
        setError(response.error || "Unable to sign up. Please try again.");
        setSubmitting(false);
      }
    } catch (error) {
      setError(error.error || "An error occurred while signing up. Please try again.");
      setSubmitting(false);
    }
  };

  const renderStepContent = () => {
    // Unregistered user (role=user from URL): event + email + participation, then about you
    if (selectedRole === "user") {
      switch (currentStep) {
        case 0:
          return (
            <div className="space-y-4">
              <TextField
                label="Email Address (Required)"
                name="email"
                type="email"
                value={formData.email}
                onChange={(e) => handleInputChange("email", e.target.value)}
                placeholder="Enter your email"
                required
              />
              {userExistsMessage && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800 flex flex-col gap-2">
                  <span>{userExistsMessage}</span>
                  <Link to="/login" className="text-brand-blue font-medium hover:underline">
                    Log in to the portal
                  </Link>
                </div>
              )}
              <div className="flex flex-col gap-1">
                <label className="text-sm font-medium text-slate-700 text-left">
                  How Would You Like to Participate? <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <select
                    value={formData.participation_type}
                    onChange={(e) => handleInputChange("participation_type", e.target.value)}
                    className="w-full rounded-lg border border-slate-200 bg-white px-4 py-2.5 pr-10 text-sm text-slate-900 shadow-sm focus:border-brand-blue focus:outline-none focus:ring-2 focus:ring-brand-blue appearance-none"
                    required
                  >
                    <option value="">Select your role</option>
                    {PARTICIPATION_TYPE_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                  <Icon
                    icon="lucide:chevron-down"
                    className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none"
                  />
                </div>
              </div>
            </div>
          );
        case 1:
          return (
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-100">
              <div className="flex items-center gap-3 mb-6">
                <div className="h-10 w-10 rounded-lg bg-blue-500 flex items-center justify-center">
                  <Icon icon="lucide:user" className="h-5 w-5 text-white" />
                </div>
                <Typography as="h3" className="text-xl font-bold text-primary-900">
                  Tell Us About You
                </Typography>
              </div>
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <TextField
                    label="Full Name (First)"
                    name="first_name"
                    value={formData.first_name}
                    onChange={(e) => handleInputChange("first_name", e.target.value)}
                    placeholder="First name"
                    required
                  />
                  <TextField
                    label="Full Name (Last)"
                    name="last_name"
                    value={formData.last_name}
                    onChange={(e) => handleInputChange("last_name", e.target.value)}
                    placeholder="Last name"
                    required
                  />
                </div>
                <div className="flex flex-col gap-1">
                  <label className="text-sm font-medium text-slate-700 text-left">
                    Phone Number <span className="text-red-500">*</span>
                  </label>
                  <PhoneVerify
                    countryCode={formData.country_code}
                    phone={formData.phone}
                    onPhoneChange={(countryCode, phone) => {
                      setFormData((prev) => ({ ...prev, country_code: countryCode, phone }));
                      setError("");
                    }}
                    onVerifiedChange={handlePhoneVerifiedChange}
                    verifiedE164={verifiedPhoneE164}
                    placeholder="Enter your phone number"
                  />
                  {!phoneVerified && (
                    <p className="text-xs text-amber-600 text-left">
                      Verify this number with the code we text you — required to continue.
                    </p>
                  )}
                </div>
                <div className="flex flex-col gap-2">
                  <label className="text-sm font-semibold text-slate-700 text-left">
                    Primary Expertise <span className="text-red-500">*</span> (select up to {MAX_MULTISELECT})
                  </label>
                  <div className="relative">
                    <select
                      multiple
                      value={formData.primary_expertise}
                      onChange={(e) => {
                        const selected = Array.from(e.target.selectedOptions, (option) => option.value);
                        handleLimitedMultiSelect("primary_expertise", selected, "Primary Expertise");
                      }}
                      className="w-full rounded-lg border border-slate-200 bg-white px-4 py-2.5 text-sm text-slate-900 focus:border-brand-blue focus:outline-none focus:ring-2 focus:ring-brand-blue min-h-[120px]"
                    >
                      {PRIMARY_EXPERTISE_OPTIONS.map((opt) => (
                        <option key={opt} value={opt}>
                          {opt}
                        </option>
                      ))}
                    </select>
                    <Typography as="p" className="text-xs text-tertiary-500 mt-1 text-left">
                      Hold Ctrl/Cmd to select up to {MAX_MULTISELECT} ({formData.primary_expertise?.length || 0} selected)
                    </Typography>
                    {formData.primary_expertise?.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-2">
                        {formData.primary_expertise.map((ex) => (
                          <span
                            key={ex}
                            className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-blue-100 text-blue-700 text-xs font-medium"
                          >
                            {ex}
                            <button
                              type="button"
                              onClick={() =>
                                handleInputChange(
                                  "primary_expertise",
                                  formData.primary_expertise.filter((e) => e !== ex)
                                )
                              }
                              className="hover:text-blue-900"
                            >
                              <Icon icon="lucide:x" className="h-3 w-3" />
                            </button>
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex flex-col gap-2">
                  <label className="text-sm font-semibold text-slate-700 text-left">
                    Intent & Interest <span className="text-red-500">*</span> (select up to {MAX_MULTISELECT})
                  </label>
                  <div className="relative">
                    <select
                      multiple
                      value={formData.preferred_roles}
                      onChange={(e) => {
                        const selected = Array.from(e.target.selectedOptions, (option) => option.value);
                        handleLimitedMultiSelect("preferred_roles", selected, "Intent & Interest");
                      }}
                      className="w-full rounded-lg border border-slate-200 bg-white px-4 py-2.5 text-sm text-slate-900 focus:border-brand-blue focus:outline-none focus:ring-2 focus:ring-brand-blue min-h-[120px]"
                    >
                      {INTENT_INTEREST_OPTIONS.map((opt) => (
                        <option key={opt} value={opt}>
                          {opt}
                        </option>
                      ))}
                    </select>
                    <Typography as="p" className="text-xs text-tertiary-500 mt-1 text-left">
                      Hold Ctrl/Cmd to select up to {MAX_MULTISELECT} ({formData.preferred_roles?.length || 0} selected)
                    </Typography>
                    {formData.preferred_roles?.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-2">
                        {formData.preferred_roles.map((item) => (
                          <span
                            key={item}
                            className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-indigo-100 text-indigo-700 text-xs font-medium"
                          >
                            {item}
                            <button
                              type="button"
                              onClick={() =>
                                handleInputChange(
                                  "preferred_roles",
                                  formData.preferred_roles.filter((i) => i !== item)
                                )
                              }
                              className="hover:text-indigo-900"
                            >
                              <Icon icon="lucide:x" className="h-3 w-3" />
                            </button>
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        default:
          return null;
      }
    }

    // Mentor/Student form (single step)
    if (selectedRole === "mentor" || selectedRole === "student") {
      const usernameHasError = typeof error === "string" && error.toLowerCase().includes("username");
      const passwordHasError = typeof error === "string" && error.toLowerCase().includes("password");
      const otpHasError =
        typeof error === "string" &&
        (error.toLowerCase().includes("verification") || error.toLowerCase().includes("otp") || error.toLowerCase().includes("code"));

      if (mentorStage === "otp") {
        return (
          <div className="space-y-4">
            <TextField
              label="Verification code"
              name="otp_code"
              value={mentorOtpCode}
              onChange={(e) => {
                const next = (e.target.value || "").replace(/\D/g, "").slice(0, 6);
                setMentorOtpCode(next);
                setError("");
              }}
              placeholder="Enter 6-digit code"
              inputMode="numeric"
              autoComplete="one-time-code"
              error={otpHasError ? error : ""}
              helperText={otpHasError ? error : "Enter the 6-digit code sent to your email."}
              required
            />

            <div className="flex flex-wrap items-center justify-between gap-2">
              <Button
                type="button"
                variant="secondary"
                size="small"
                disabled={submitting}
                onClick={async () => {
                  try {
                    setError("");
                    setSubmitting(true);
                    const sendRes = await sendEmailOtp({ email: mentorOtpEmail || formData.email?.trim?.() || "" });
                    const otpSent = sendRes?.success && sendRes?.data?.success !== false;
                    if (!otpSent) {
                      setError(sendRes?.data?.message || sendRes?.error || "Unable to resend OTP. Please try again.");
                    }
                  } finally {
                    setSubmitting(false);
                  }
                }}
              >
                Resend code
              </Button>

              <Button
                type="button"
                variant="secondary"
                size="small"
                disabled={submitting}
                onClick={() => {
                  setError("");
                  setMentorOtpCode("");
                  setMentorStage("form");
                }}
              >
                Change email
              </Button>
            </div>
          </div>
        );
      }

      return (
        <div className="space-y-4">
          <TextField
            label="Email"
            name="email"
            type="email"
            value={formData.email}
            onChange={(e) => handleInputChange("email", e.target.value)}
            onBlur={handleEmailBlur}
            placeholder="Enter your email"
            error={emailExistsError}
            helperText={emailExistsError}
            required
          />
          <TextField
            label="Username"
            name="username"
            value={formData.username}
            onChange={(e) => handleInputChange("username", e.target.value)}
            onBlur={handleUsernameBlur}
            placeholder="e.g., jane_doe"
            error={usernameExistsError || (usernameHasError ? error : "")}
            helperText={
              usernameExistsError || (usernameHasError ? error : "") ||
              "3–30 characters. Use letters, numbers, '.', '_', or '-'. No spaces."
            }
            required
          />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <TextField
              label="First Name"
              name="first_name"
              value={formData.first_name}
              onChange={(e) => handleInputChange("first_name", e.target.value)}
              placeholder="Enter your first name"
              required
            />
            <TextField
              label="Last Name"
              name="last_name"
              value={formData.last_name}
              onChange={(e) => handleInputChange("last_name", e.target.value)}
              placeholder="Enter your last name"
              required
            />
          </div>
          <TextField
            label="Password"
            name="password"
            type="password"
            value={formData.password}
            onChange={(e) => handleInputChange("password", e.target.value)}
            placeholder="Enter your password"
            error={passwordFieldError || (passwordHasError ? error : "")}
            helperText={
              passwordFieldError || (passwordHasError ? error : "") ||
              "At least 8 characters with uppercase, lowercase, number, and a special character (e.g. #). No spaces."
            }
            required
            showPasswordToggle
          />
          <TextField
            label="Re-enter Password"
            name="re_enter_password"
            type="password"
            value={formData.re_enter_password}
            onChange={(e) => handleInputChange("re_enter_password", e.target.value)}
            placeholder="Enter your password again"
            error={reEnterPasswordFieldError || (passwordHasError ? error : "")}
            helperText={reEnterPasswordFieldError || (passwordHasError ? error : "")}
            required
            showPasswordToggle
          />
          <p className="text-sm text-slate-600 text-left -mb-1">
            One more step — your phone number is required to complete sign-up.
          </p>
          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium text-slate-700 text-left">
              What is your phone number? <span className="text-red-500">*</span>
            </label>
            <PhoneVerify
              countryCode={formData.country_code}
              phone={formData.phone_number}
              onPhoneChange={(countryCode, phone) => {
                setFormData((prev) => ({ ...prev, country_code: countryCode, phone_number: phone }));
                setError("");
              }}
              onVerifiedChange={handlePhoneVerifiedChange}
              verifiedE164={verifiedPhoneE164}
              placeholder="Enter your phone number"
              required
            />
            {!phoneVerified && (
              <p className="text-xs text-amber-600 text-left">
                Verify this number with the code we text you — required to complete sign-up.
              </p>
            )}
          </div>
        </div>
      );
    }

    // Partner/Investor form (2-step)
    if (isPartnerOrInvestorRole(selectedRole)) {
      const partnerInvestorOtpHasError =
        typeof error === "string" &&
        (error.toLowerCase().includes("verification") || error.toLowerCase().includes("otp") || error.toLowerCase().includes("code"));

      if (currentStep === 1 && partnerInvestorStage === "otp") {
        return (
          <div className="space-y-4">
            <Typography as="p" className="text-sm text-tertiary-600 text-left mb-2">
              We sent a 6-digit code to {partnerInvestorOtpEmail || formData.contact_email}. Enter it below.
            </Typography>
            <TextField
              label="Verification code"
              name="otp_code"
              value={partnerInvestorOtpCode}
              onChange={(e) => {
                const next = (e.target.value || "").replace(/\D/g, "").slice(0, 6);
                setPartnerInvestorOtpCode(next);
                setError("");
              }}
              placeholder="Enter 6-digit code"
              inputMode="numeric"
              autoComplete="one-time-code"
              error={partnerInvestorOtpHasError ? error : ""}
              helperText={partnerInvestorOtpHasError ? error : "Enter the 6-digit code sent to your email."}
              required
            />
            <div className="flex flex-wrap items-center justify-between gap-2">
              <Button
                type="button"
                variant="secondary"
                size="small"
                disabled={submitting}
                onClick={async () => {
                  try {
                    setError("");
                    setSubmitting(true);
                    const sendRes = await sendEmailOtp({ email: partnerInvestorOtpEmail || formData.contact_email?.trim() || "" });
                    const otpSent = sendRes?.success && sendRes?.data?.success !== false;
                    if (!otpSent) {
                      setError(sendRes?.data?.message || sendRes?.error || "Unable to resend OTP. Please try again.");
                    }
                  } finally {
                    setSubmitting(false);
                  }
                }}
              >
                Resend code
              </Button>
              <Button
                type="button"
                variant="secondary"
                size="small"
                disabled={submitting}
                onClick={() => {
                  setError("");
                  setPartnerInvestorOtpCode("");
                  setPartnerInvestorStage("form");
                }}
              >
                Change email
              </Button>
            </div>
          </div>
        );
      }

      switch (currentStep) {
        case 0: // Step 1: Basics
          return (
            <div className="space-y-4">
              <TextField
                label={selectedRole === "investor" ? "Investment Entity" : "Company Name"}
                name="sponsor_name"
                value={formData.sponsor_name}
                onChange={(e) => handleInputChange("sponsor_name", e.target.value)}
                placeholder={selectedRole === "investor" ? "Enter the investment entity name" : "Enter the company name"}
                required
                autoFocus
              />
              <TextField
                label={
                  selectedRole === "investor"
                    ? "Registered Fund Name"
                    : selectedRole === "ecosystem_partner"
                      ? "Registered Corporate Entity"
                      : "Legal Entity Name"
                }
                name="legal_entity_name"
                value={formData.legal_entity_name}
                onChange={(e) => handleInputChange("legal_entity_name", e.target.value)}
                placeholder={
                  selectedRole === "investor"
                    ? "Enter the registered fund name"
                    : selectedRole === "ecosystem_partner"
                      ? "Enter the registered corporate entity"
                      : "Enter the legal entity name"
                }
                required
              />
              <div className="flex flex-col gap-1">
                <label className="text-sm font-medium text-slate-700 text-left" htmlFor="signup-website-url">
                  {selectedRole === "investor" ? "Portfolio / Firm URL" : "Corporate Portal"}{" "}
                  <span className="text-red-500">*</span>
                </label>
                <GeminiAutocomplete
                  inputId="signup-website-url"
                  label={selectedRole === "investor" ? "Portfolio / Firm URL" : "Corporate Portal"}
                  value={formData.website_url}
                  onChange={(value) => handleInputChange("website_url", value)}
                  placeholder={
                    selectedRole === "investor"
                      ? "e.g. microsoft.com or https://www.google.com"
                      : "e.g. portal.example.com or https://www.example.com"
                  }
                  helperText="Type for AI suggestions. You can enter a domain or a full https:// URL."
                />
              </div>
              {selectedRole === "investor" && (
                <div className="flex flex-col gap-2">
                  <div>
                    <label className="text-sm font-medium text-slate-700 text-left" htmlFor="signup-investment-profile">
                      Investment Profile <span className="text-red-500">*</span>
                    </label>
                    <Typography as="p" className="text-xs text-tertiary-600 text-left mt-1">
                      Select the option that best describes how you invest or support ventures.
                    </Typography>
                  </div>
                  <div className="relative">
                    <select
                      id="signup-investment-profile"
                      name="investment_profile"
                      value={formData.investment_profile || ""}
                      onChange={(e) => handleInputChange("investment_profile", e.target.value)}
                      required
                      className="w-full rounded-lg border border-slate-200 bg-white px-4 py-2.5 pr-10 text-sm text-slate-900 shadow-sm focus:border-brand-blue focus:outline-none focus:ring-2 focus:ring-brand-blue appearance-none"
                    >
                      <option value="">Select your investment profile</option>
                      {INVESTMENT_PROFILE_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.hint ? `${opt.label} — ${opt.hint}` : opt.label}
                        </option>
                      ))}
                    </select>
                    <Icon
                      icon="lucide:chevron-down"
                      className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none"
                    />
                  </div>
                </div>
              )}
              {selectedRole === "corporate_sponsor" && (
                <div className="flex flex-col gap-2">
                  <div>
                    <label className="text-sm font-medium text-slate-700 text-left">
                      Partnership Tier Selection <span className="text-red-500">*</span>
                    </label>
                    <Typography as="p" className="text-xs text-tertiary-600 text-left mt-1">
                      Tiers determine the level of brand visibility, VIP access, and recognition throughout the program journey.
                    </Typography>
                  </div>
                  <div className="relative">
                    <select
                      name="funding_amount"
                      value={formData.funding_amount || ""}
                      onChange={(e) => handleInputChange("funding_amount", e.target.value)}
                      required
                      className="w-full rounded-lg border border-slate-200 bg-white px-4 py-2.5 pr-10 text-sm text-slate-900 shadow-sm focus:border-brand-blue focus:outline-none focus:ring-2 focus:ring-brand-blue appearance-none"
                    >
                      <option value="">Select the tier that aligns with your strategic goals.</option>
                      {FUNDING_TIER_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                    <Icon
                      icon="lucide:chevron-down"
                      className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none"
                    />
                  </div>
                </div>
              )}
            </div>
          );

        case 1: {
          // Step 2: Contact Details (investor, ecosystem_partner, corporate_sponsor)
          const verifyPasswordFormError =
            error === "Passwords do not match" || error === "Verify Password is required" ? error : "";
          return (
            <div className="space-y-4">
              {isPartnerOrInvestorRole(selectedRole) && (
                <div className="text-left pb-2 border-b border-slate-200">
                  <Typography as="h3" className="text-base font-semibold text-primary-900">
                    Direct Engagement Points
                  </Typography>
                  <Typography as="p" className="text-sm text-tertiary-600 mt-1">
                    Ensure we have the right channels to coordinate and collaborate on initiatives.
                  </Typography>
                </div>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <TextField
                  label={isPartnerLikeRole(selectedRole) ? "First Name" : "First name"}
                  name="first_name"
                  value={formData.first_name}
                  onChange={(e) => handleInputChange("first_name", e.target.value)}
                  placeholder="Enter first name"
                  required
                  autoFocus
                />
                <TextField
                  label={isPartnerLikeRole(selectedRole) ? "Last Name" : "Last name"}
                  name="last_name"
                  value={formData.last_name}
                  onChange={(e) => handleInputChange("last_name", e.target.value)}
                  placeholder="Enter last name"
                  required
                />
              </div>
              <TextField
                label="Username"
                name="username"
                value={formData.username}
                onChange={(e) => handleInputChange("username", e.target.value)}
                onBlur={handleUsernameBlur}
                placeholder="Enter username"
                required
                error={!!usernameExistsError}
                helperText={usernameExistsError}
              />
              <TextField
                label="Password"
                name="password"
                type="password"
                value={formData.password}
                onChange={(e) => handleInputChange("password", e.target.value)}
                placeholder="Min 8 characters, include uppercase, lowercase, number and special character"
                required
                error={!!passwordFieldError}
                helperText={passwordFieldError}
                showPasswordToggle
              />
              <TextField
                label="Verify Password"
                name="re_enter_password"
                type="password"
                value={formData.re_enter_password}
                onChange={(e) => handleInputChange("re_enter_password", e.target.value)}
                placeholder="Re-enter your password"
                required
                error={!!reEnterPasswordFieldError || !!verifyPasswordFormError}
                helperText={reEnterPasswordFieldError || verifyPasswordFormError}
                showPasswordToggle
              />
              <TextField
                label={
                  selectedRole === "corporate_sponsor"
                    ? "Your Role within the Organization"
                    : isPartnerOrInvestorRole(selectedRole)
                      ? "Designation / Title"
                      : "Contact Role"
                }
                name="contact_role"
                value={formData.contact_role}
                onChange={(e) => handleInputChange("contact_role", e.target.value)}
                placeholder={isPartnerOrInvestorRole(selectedRole) ? "Enter the contact role here" : "Enter the contact role"}
                required
              />
              <TextField
                label={selectedRole === "corporate_sponsor" ? "Professional Email Address" : isPartnerLikeRole(selectedRole) ? "Email Address" : "Contact Email"}
                name="contact_email"
                type="email"
                value={formData.contact_email}
                onChange={(e) => handleInputChange("contact_email", e.target.value)}
                onBlur={handleContactEmailBlur}
                placeholder={
                  isPartnerLikeRole(selectedRole) ? "Enter email address" : "Enter the contact email address"
                }
                required
                error={!!emailExistsError}
                helperText={emailExistsError}
              />
              <div className="flex flex-col gap-1">
                <label className="text-sm font-medium text-slate-700 text-left">
                  {isPartnerOrInvestorRole(selectedRole) ? "Direct Line" : "Contact Phone"}{" "}
                  <span className="text-red-500">*</span>
                </label>
                <PhoneVerify
                  countryCode={formData.contact_country_code}
                  phone={formData.contact_phone}
                  onPhoneChange={(countryCode, phone) => {
                    setFormData((prev) => ({ ...prev, contact_country_code: countryCode, contact_phone: phone }));
                    setError("");
                  }}
                  onVerifiedChange={handlePhoneVerifiedChange}
                  verifiedE164={verifiedPhoneE164}
                  placeholder={isPartnerOrInvestorRole(selectedRole) ? "Enter the direct line number here" : "Enter the contact phone number"}
                  required
                />
                {!phoneVerified && (
                  <p className="text-xs text-amber-600 text-left">
                    Verify this number with the code we text you — required to continue.
                  </p>
                )}
              </div>
              {!["investor", "ecosystem_partner", "corporate_sponsor"].includes(selectedRole) && (
              <div className="border-t border-slate-200 pt-4 mt-4">
                {!showSecondaryContact ? (
                  <button
                    type="button"
                    onClick={() => setShowSecondaryContact(true)}
                    className="text-sm font-medium text-brand-blue hover:text-brand-blue/80"
                  >
                    + Add secondary contact
                  </button>
                ) : (
                  <>
                    <div className="flex items-center justify-between mb-4">
                      <Typography as="p" className="text-sm font-medium text-slate-700">Secondary Contact</Typography>
                      <button
                        type="button"
                        onClick={() => setShowSecondaryContact(false)}
                        className="text-sm text-slate-500 hover:text-slate-700"
                      >
                        Remove
                      </button>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <TextField
                        label="First name"
                        name="contact_2_first_name"
                        value={formData.contact_2_first_name}
                        onChange={(e) => handleInputChange("contact_2_first_name", e.target.value)}
                        placeholder="Enter first name"
                      />
                      <TextField
                        label="Last name"
                        name="contact_2_last_name"
                        value={formData.contact_2_last_name}
                        onChange={(e) => handleInputChange("contact_2_last_name", e.target.value)}
                        placeholder="Enter last name"
                      />
                    </div>
                    <TextField
                      label="Username"
                      name="contact_2_username"
                      value={formData.contact_2_username}
                      onChange={(e) => handleInputChange("contact_2_username", e.target.value)}
                      placeholder="Enter username"
                      className="mt-4"
                    />
                    <TextField
                      label="Password"
                      name="contact_2_password"
                      type="password"
                      value={formData.contact_2_password}
                      onChange={(e) => handleInputChange("contact_2_password", e.target.value)}
                      placeholder="Min 8 characters, include uppercase, lowercase, number and special character"
                      className="mt-4"
                    />
                    <TextField
                      label="Contact Role"
                      name="contact_2_contact_role"
                      value={formData.contact_2_contact_role}
                      onChange={(e) => handleInputChange("contact_2_contact_role", e.target.value)}
                      placeholder="Enter the contact role"
                      className="mt-4"
                    />
                    <TextField
                      label="Contact Email"
                      name="contact_2_email"
                      type="email"
                      value={formData.contact_2_email}
                      onChange={(e) => handleInputChange("contact_2_email", e.target.value)}
                      placeholder="Enter the contact email address"
                      className="mt-4"
                    />
                    <div className="flex flex-col gap-1 mt-4">
                      <label className="text-sm font-medium text-slate-700 text-left">Contact Phone</label>
                      {/* Secondary contact is a colleague, not the person filling
                          this form — they can't receive the SMS, so no OTP here. */}
                      <PhoneVerify
                        countryCode={formData.contact_2_country_code}
                        phone={formData.contact_2_phone}
                        onPhoneChange={(countryCode, phone) => {
                          setFormData((prev) => ({ ...prev, contact_2_country_code: countryCode, contact_2_phone: phone }));
                        }}
                        showVerify={false}
                        placeholder="Enter the contact phone number"
                      />
                    </div>
                  </>
                )}
              </div>
              )}
            </div>
          );
        }

        default:
          return null;
      }
    }

    // Volunteer form (multi-step)
    switch (currentStep) {
      case 0: // Account Information
        return (
          <div className="space-y-4">
            <div className="flex flex-col gap-1">
              <label className="text-sm font-medium text-slate-700 text-left">
                Event <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <select
                  value={formData.event_id}
                  onChange={(e) => handleInputChange("event_id", e.target.value)}
                  className="w-full rounded-lg border border-slate-200 bg-white px-4 py-2.5 pr-10 text-sm text-slate-900 shadow-sm focus:border-brand-blue focus:outline-none focus:ring-2 focus:ring-brand-blue appearance-none"
                  required
                  autoFocus
                  disabled={loadingEvents}
                >
                  <option value="">{loadingEvents ? "Loading events..." : "Select an event"}</option>
                  {events.map((event) => (
                    <option key={event.id} value={event.id}>
                      {event.title}
                    </option>
                  ))}
                </select>
                <Icon
                  icon="lucide:chevron-down"
                  className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none"
                />
              </div>
            </div>
            <TextField
              label="Email"
              name="email"
              type="email"
              value={formData.email}
              onChange={(e) => handleInputChange("email", e.target.value)}
              placeholder="Enter your email"
              required
            />
          </div>
        );

      case 1: // Identity
        return (
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-100">
            <div className="flex items-center gap-3 mb-6">
              <div className="h-10 w-10 rounded-lg bg-blue-500 flex items-center justify-center">
                <Icon icon="lucide:user" className="h-5 w-5 text-white" />
              </div>
              <Typography as="h3" className="text-xl font-bold text-primary-900">
                Identity
              </Typography>
            </div>
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <TextField
                  label="First Name"
                  name="first_name"
                  value={formData.first_name}
                  onChange={(e) => handleInputChange("first_name", e.target.value)}
                  placeholder="Enter your first name"
                  required
                />
                <TextField
                  label="Last Name"
                  name="last_name"
                  value={formData.last_name}
                  onChange={(e) => handleInputChange("last_name", e.target.value)}
                  placeholder="Enter your last name"
                  required
                />
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-sm font-medium text-slate-700 text-left">
                  Current Occupation <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <select
                    value={formData.current_occupation}
                    onChange={(e) => handleInputChange("current_occupation", e.target.value)}
                    className="w-full rounded-lg border border-slate-200 bg-white px-4 py-2.5 pr-10 text-sm text-slate-900 shadow-sm focus:border-brand-blue focus:outline-none focus:ring-2 focus:ring-brand-blue appearance-none"
                    required
                  >
                    <option value="">Select occupation</option>
                    <option value="Student">Student</option>
                    <option value="Professional">Professional</option>
                    <option value="Academic">Academic</option>
                    <option value="Retiree">Retiree</option>
                    <option value="Entrepreneur">Entrepreneur</option>
                  </select>
                  <Icon
                    icon="lucide:chevron-down"
                    className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none"
                  />
                </div>
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-sm font-medium text-slate-700 text-left">
                  Organization/Employer/Institute
                </label>
                <GeminiAutocomplete
                  label="Organization/Employer/Institute"
                  value={formData.organization_employer}
                  onChange={(value) => handleInputChange("organization_employer", value)}
                  placeholder="Enter your current company, university, or institute"
                />
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-sm font-medium text-slate-700 text-left">
                  Job Title / Major
                </label>
                <GeminiAutocomplete
                  label="Job Title / Major"
                  value={formData.job_title_major}
                  onChange={(value) => handleInputChange("job_title_major", value)}
                  placeholder="Enter your professional title or field of study"
                />
              </div>
              <TextField
                label="LinkedIn Profile"
                name="linkedin_profile"
                type="url"
                value={formData.linkedin_profile}
                onChange={(e) => handleInputChange("linkedin_profile", e.target.value)}
                placeholder="https://linkedin.com/in/yourprofile"
              />
              <TextField
                label="Years of Experience"
                name="years_of_experience"
                type="number"
                value={formData.years_of_experience}
                onChange={(e) => handleInputChange("years_of_experience", e.target.value)}
                placeholder="Enter years of relevant experience"
                required
                min="0"
              />
              <div className="flex flex-col gap-1">
                <label className="text-sm font-medium text-slate-700 text-left">
                  Phone
                </label>
                <PhoneVerify
                  countryCode={formData.country_code}
                  phone={formData.phone}
                  onPhoneChange={(countryCode, phone) => {
                    setFormData((prev) => ({ ...prev, country_code: countryCode, phone }));
                    setError("");
                  }}
                  onVerifiedChange={handlePhoneVerifiedChange}
                  verifiedE164={verifiedPhoneE164}
                  placeholder="Enter your phone number"
                />
                {formData.phone && !phoneVerified && (
                  <p className="text-xs text-amber-600 text-left">
                    Verify this number with the code we text you — required if you provide a phone.
                  </p>
                )}
                {/* <p className="text-xs text-slate-500">10-15 digits</p> */}
              </div>
            </div>
          </div>
        );

      case 2: // Skills & Role
        return (
          <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl p-6 border border-purple-100">
            <div className="flex items-center gap-3 mb-6">
              <div className="h-10 w-10 rounded-lg bg-purple-500 flex items-center justify-center">
                <Icon icon="lucide:briefcase" className="h-5 w-5 text-white" />
              </div>
              <Typography as="h3" className="text-xl font-bold text-primary-900">
                Skills & Role
              </Typography>
            </div>
            <div className="space-y-4">
              <div className="flex flex-col gap-2">
                <label className="text-sm font-semibold text-slate-700 text-left">
                  Primary Expertise <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <select
                    multiple
                    value={formData.primary_expertise}
                    onChange={(e) => {
                      const selected = Array.from(e.target.selectedOptions, option => option.value);
                      handleLimitedMultiSelect("primary_expertise", selected, "Primary Expertise");
                    }}
                    className="w-full rounded-lg border border-slate-200 bg-white px-4 py-2.5 pr-10 text-sm text-slate-900 shadow-sm focus:border-brand-blue focus:outline-none focus:ring-2 focus:ring-brand-blue min-h-[100px]"
                    required
                  >
                    <option value="Technical & Engineering">Technical & Engineering</option>
                    <option value="Business & Strategy">Business & Strategy</option>
                    <option value="Marketing & Communications">Marketing & Communications</option>
                    <option value="Operations & Logistics">Operations & Logistics</option>
                    <option value="Creative & Design">Creative & Design</option>
                    <option value="Finance">Finance</option>
                    <option value="Legal">Legal</option>
                  </select>
                  <Typography as="p" className="text-xs text-tertiary-500 mt-1 text-left">
                    Hold Ctrl/Cmd to select up to {MAX_MULTISELECT} ({formData.primary_expertise.length} selected)
                  </Typography>
                  {formData.primary_expertise.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-2">
                      {formData.primary_expertise.map((expertise) => (
                        <span
                          key={expertise}
                          className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-purple-100 text-purple-700 text-xs font-medium"
                        >
                          {expertise}
                          <button
                            type="button"
                            onClick={() => {
                              handleInputChange(
                                "primary_expertise",
                                formData.primary_expertise.filter(e => e !== expertise)
                              );
                            }}
                            className="hover:text-purple-900"
                          >
                            <Icon icon="lucide:x" className="h-3 w-3" />
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              <div className="flex flex-col gap-2">
                <label className="text-sm font-semibold text-slate-700 text-left">
                  Preferred Role <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <select
                    multiple
                    value={formData.preferred_role}
                    onChange={(e) => {
                      const selected = Array.from(e.target.selectedOptions, option => option.value);
                      handleLimitedMultiSelect("preferred_role", selected, "Preferred Role");
                    }}
                    className="w-full rounded-lg border border-slate-200 bg-white px-4 py-2.5 pr-10 text-sm text-slate-900 shadow-sm focus:border-brand-blue focus:outline-none focus:ring-2 focus:ring-brand-blue min-h-[100px]"
                    required
                  >
                    <option value="Tech Host">Tech Host</option>
                    <option value="Event Success Lead">Event Success Lead</option>
                    <option value="Hackathon Facilitator">Hackathon Facilitator</option>
                    <option value="Mentor Liaison">Mentor Liaison</option>
                    <option value="Research & Insights">Research & Insights</option>
                    <option value="Social Media Management">Social Media Management</option>
                    <option value="Content Creator">Content Creator</option>
                    <option value="Community Engagement Lead">Community Engagement Lead</option>
                    <option value="Help Desk Specialist">Help Desk Specialist</option>
                    <option value="Program Coordinator">Program Coordinator</option>
                  </select>
                  <Typography as="p" className="text-xs text-tertiary-500 mt-1 text-left">
                    Hold Ctrl/Cmd to select up to {MAX_MULTISELECT} ({formData.preferred_role.length} selected)
                  </Typography>
                  {formData.preferred_role.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-2">
                      {formData.preferred_role.map((role) => (
                        <span
                          key={role}
                          className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-green-100 text-green-700 text-xs font-medium"
                        >
                          {role}
                          <button
                            type="button"
                            onClick={() => {
                              handleInputChange(
                                "preferred_role",
                                formData.preferred_role.filter(r => r !== role)
                              );
                            }}
                            className="hover:text-green-900"
                          >
                            <Icon icon="lucide:x" className="h-3 w-3" />
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              <div className="flex flex-col gap-2">
                <label className="text-sm font-semibold text-slate-700 text-left">
                  Technical Stack
                </label>
                <div className="relative">
                  <select
                    multiple
                    value={formData.technical_stack}
                    onChange={(e) => {
                      const selected = Array.from(e.target.selectedOptions, option => option.value);
                      handleLimitedMultiSelect("technical_stack", selected, "Technical Stack");
                    }}
                    className="w-full rounded-lg border border-slate-200 bg-white px-4 py-2.5 pr-10 text-sm text-slate-900 shadow-sm focus:border-brand-blue focus:outline-none focus:ring-2 focus:ring-brand-blue min-h-[100px]"
                  >
                    <option value="Web Development (Frontend, Backend, Full-stack)">Web Development (Frontend, Backend, Full-stack)</option>
                    <option value="Data & AI (Data Science, Machine Learning, SQL)">Data & AI (Data Science, Machine Learning, SQL)</option>
                    <option value="No-Code/Low-Code (Webflow, Zapier, Airtable, Bubble)">No-Code/Low-Code (Webflow, Zapier, Airtable, Bubble)</option>
                    <option value="Mobile Dev (iOS, Android, React Native)">Mobile Dev (iOS, Android, React Native)</option>
                    <option value="Product & Design (Figma, Adobe Suite, UI/UX)">Product & Design (Figma, Adobe Suite, UI/UX)</option>
                    <option value="Infrastructure (Cloud/AWS, DevOps, Security)">Infrastructure (Cloud/AWS, DevOps, Security)</option>
                  </select>
                  <Typography as="p" className="text-xs text-tertiary-500 mt-1 text-left">
                    Hold Ctrl/Cmd to select up to {MAX_MULTISELECT} ({formData.technical_stack.length} selected)
                  </Typography>
                  {formData.technical_stack.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-2">
                      {formData.technical_stack.map((stack) => (
                        <span
                          key={stack}
                          className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-blue-100 text-blue-700 text-xs font-medium"
                        >
                          {stack}
                          <button
                            type="button"
                            onClick={() => {
                              handleInputChange(
                                "technical_stack",
                                formData.technical_stack.filter(s => s !== stack)
                              );
                            }}
                            className="hover:text-blue-900"
                          >
                            <Icon icon="lucide:x" className="h-3 w-3" />
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        );

      case 3: // Commitment
        return (
          <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-6 border border-green-100">
            <div className="flex items-center gap-3 mb-6">
              <div className="h-10 w-10 rounded-lg bg-green-500 flex items-center justify-center">
                <Icon icon="lucide:calendar" className="h-5 w-5 text-white" />
              </div>
              <Typography as="h3" className="text-xl font-bold text-primary-900">
                Commitment
              </Typography>
            </div>
            <div className="space-y-4">
              <div className="flex flex-col gap-2">
                <label className="text-sm font-medium text-slate-700 text-left">
                  Weekly Availability <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <select
                    value={formData.weekly_availability}
                    onChange={(e) => handleInputChange("weekly_availability", e.target.value)}
                    className="w-full rounded-lg border border-slate-200 bg-white px-4 py-2.5 pr-10 text-sm text-slate-900 shadow-sm focus:border-brand-blue focus:outline-none focus:ring-2 focus:ring-brand-blue appearance-none"
                    required
                  >
                    <option value="">Select weekly availability</option>
                    <option value="2 hours">2 hours</option>
                    <option value="4 hours">4 hours</option>
                    <option value="8 hours">8 hours</option>
                    <option value="16 hours">16 hours</option>
                  </select>
                  <Icon
                    icon="lucide:chevron-down"
                    className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none"
                  />
                </div>
              </div>
              <div className="flex flex-col gap-2">
                <label className="text-sm font-semibold text-slate-700 text-left">
                  Timezone <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <select
                    value={formData.timezone}
                    onChange={(e) => handleInputChange("timezone", e.target.value)}
                    className="w-full rounded-lg border border-slate-200 bg-white px-4 py-2.5 pr-10 text-sm text-slate-900 shadow-sm focus:border-brand-blue focus:outline-none focus:ring-2 focus:ring-brand-blue appearance-none"
                    required
                  >
                    <option value="">Select timezone</option>
                    <option value="UTC-12">UTC-12</option>
                    <option value="UTC-11">UTC-11</option>
                    <option value="UTC-10">UTC-10</option>
                    <option value="UTC-9">UTC-9</option>
                    <option value="UTC-8">UTC-8 (PST)</option>
                    <option value="UTC-7">UTC-7 (MST)</option>
                    <option value="UTC-6">UTC-6 (CST)</option>
                    <option value="UTC-5">UTC-5 (EST)</option>
                    <option value="UTC-4">UTC-4</option>
                    <option value="UTC-3">UTC-3</option>
                    <option value="UTC-2">UTC-2</option>
                    <option value="UTC-1">UTC-1</option>
                    <option value="UTC+0">UTC+0 (GMT)</option>
                    <option value="UTC+1">UTC+1 (CET)</option>
                    <option value="UTC+2">UTC+2</option>
                    <option value="UTC+3">UTC+3</option>
                    <option value="UTC+4">UTC+4</option>
                    <option value="UTC+5">UTC+5 (IST)</option>
                    <option value="UTC+6">UTC+6</option>
                    <option value="UTC+7">UTC+7</option>
                    <option value="UTC+8">UTC+8 (CST)</option>
                    <option value="UTC+9">UTC+9 (JST)</option>
                    <option value="UTC+10">UTC+10</option>
                    <option value="UTC+11">UTC+11</option>
                    <option value="UTC+12">UTC+12</option>
                  </select>
                  <Icon
                    icon="lucide:chevron-down"
                    className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none"
                  />
                </div>
              </div>
              <div className="flex flex-col gap-2">
                <label className="text-sm font-semibold text-slate-700 text-left">
                  Commitment Length <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <select
                    value={formData.commitment_length}
                    onChange={(e) => handleInputChange("commitment_length", e.target.value)}
                    className="w-full rounded-lg border border-slate-200 bg-white px-4 py-2.5 pr-10 text-sm text-slate-900 shadow-sm focus:border-brand-blue focus:outline-none focus:ring-2 focus:ring-brand-blue appearance-none"
                    required
                  >
                    <option value="">Select commitment length</option>
                    <option value="One-time event">One-time event</option>
                    <option value="Semester-long">Semester-long</option>
                    <option value="Indefinite">Indefinite</option>
                  </select>
                  <Icon
                    icon="lucide:chevron-down"
                    className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none"
                  />
                </div>
              </div>
              <div className="flex flex-col gap-2">
                <label className="text-sm font-semibold text-slate-700 text-left">
                  Communication Preference <span className="text-red-500">*</span>
                </label>
                <div className="flex gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      name="communication_preference"
                      value="Email"
                      checked={formData.communication_preference.includes("Email")}
                      onChange={(e) => {
                        const currentPreferences = formData.communication_preference || [];
                        if (e.target.checked) {
                          handleInputChange("communication_preference", [...currentPreferences, "Email"]);
                        } else {
                          handleInputChange("communication_preference", currentPreferences.filter(pref => pref !== "Email"));
                        }
                      }}
                      className="w-4 h-4 text-brand-blue border-slate-300 focus:ring-brand-blue rounded"
                    />
                    <span className="text-sm text-slate-700">Email</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      name="communication_preference"
                      value="SMS/WhatsApp"
                      checked={formData.communication_preference.includes("SMS/WhatsApp")}
                      onChange={(e) => {
                        const currentPreferences = formData.communication_preference || [];
                        if (e.target.checked) {
                          // Require consent before enabling SMS/WhatsApp
                          setCommunicationConsentPending(true);
                          setShowCommunicationConsent(true);
                        } else {
                          handleInputChange(
                            "communication_preference",
                            currentPreferences.filter((pref) => pref !== "SMS/WhatsApp")
                          );
                        }
                      }}
                      className="w-4 h-4 text-brand-blue border-slate-300 focus:ring-brand-blue rounded"
                    />
                    <span className="text-sm text-slate-700">SMS/WhatsApp</span>
                  </label>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setCommunicationConsentPending(false);
                    setShowCommunicationConsent(true);
                  }}
                  className="text-xs text-brand-blue hover:underline text-left w-fit"
                >
                  View consent message
                </button>
              </div>
            </div>
          </div>
        );

      case 4: // Motivation
        return (
          <div className="bg-gradient-to-r from-orange-50 to-amber-50 rounded-xl p-6 border border-orange-100">
            <div className="flex items-center gap-3 mb-6">
              <div className="h-10 w-10 rounded-lg bg-orange-500 flex items-center justify-center">
                <Icon icon="lucide:heart-handshake" className="h-5 w-5 text-white" />
              </div>
              <Typography as="h3" className="text-xl font-bold text-primary-900">
                Motivation
              </Typography>
            </div>
            <div className="space-y-4">
              <TextField
                label="Motivation Statement"
                name="motivation_statement"
                multiline
                rows={6}
                maxLength={150}
                value={formData.motivation_statement}
                onChange={(e) => handleInputChange("motivation_statement", e.target.value)}
                placeholder="Why do you want to support student innovation? (150 characters max)"
                helperText={`${(formData.motivation_statement || "").length}/150 characters`}
                required
              />
              <TextField
                label="Previous Volunteering"
                name="previous_volunteering"
                multiline
                rows={4}
                maxLength={150}
                value={formData.previous_volunteering}
                onChange={(e) => handleInputChange("previous_volunteering", e.target.value)}
                placeholder="Describe any past experience with non-profits or mentoring (150 characters max)"
                helperText={`${(formData.previous_volunteering || "").length}/150 characters`}
              />
            </div>
          </div>
        );

      case 5: // Compliance
        return (
          <div className="bg-gradient-to-r from-red-50 to-rose-50 rounded-xl p-6 border border-red-100">
            <div className="flex items-center gap-3 mb-6">
              <div className="h-10 w-10 rounded-lg bg-red-500 flex items-center justify-center">
                <Icon icon="lucide:shield-check" className="h-5 w-5 text-white" />
              </div>
              <Typography as="h3" className="text-xl font-bold text-primary-900">
                Compliance
              </Typography>
            </div>
            <div className="space-y-4">
              <div className="flex items-start gap-3 bg-white rounded-lg p-4 border border-slate-200">
                <input
                  type="checkbox"
                  id="code_of_conduct"
                  checked={formData.code_of_conduct}
                  onChange={(e) => handleInputChange("code_of_conduct", e.target.checked)}
                  disabled={!hasViewedCodeOfConduct}
                  className={`mt-1 w-5 h-5 text-brand-blue border-slate-300 rounded focus:ring-brand-blue focus:ring-2 ${
                    !hasViewedCodeOfConduct ? "opacity-50 cursor-not-allowed" : ""
                  }`}
                  required
                />
                <label 
                  htmlFor="code_of_conduct" 
                  className="text-sm text-slate-700 flex-1 text-left cursor-pointer"
                >
                  I agree to the{" "}
                  <a
                    href="/code-of-conduct"
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => {
                      e.preventDefault();
                      setShowCodeOfConduct(true);
                    }}
                    className="text-brand-blue hover:underline"
                  >
                    Code of Conduct and Non-Profit ethics
                  </a>
                  {" "}and student safety policies{" "}
                  {!hasViewedCodeOfConduct && (
                    <span className="text-xs text-slate-500 block mt-1">
                      (Please read the Code of Conduct first)
                    </span>
                  )}
                  <span className="text-red-500 font-semibold"> *</span>
                </label>
              </div>

              <div className="flex items-start gap-3 bg-white rounded-lg p-4 border border-slate-200">
                <input
                  type="checkbox"
                  id="volunteer_role_acknowledgement"
                  checked={formData.volunteer_role_acknowledgement}
                  onChange={(e) => handleInputChange("volunteer_role_acknowledgement", e.target.checked)}
                  className="mt-1 w-5 h-5 text-brand-blue border-slate-300 rounded focus:ring-brand-blue focus:ring-2"
                  required
                />
                <label
                  htmlFor="volunteer_role_acknowledgement"
                  className="text-sm text-slate-700 cursor-pointer flex-1 text-left"
                >
                  I understand that this is a volunteer role and does not imply employment or compensation.
                  <span className="text-red-500 font-semibold"> *</span>
                </label>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen min-h-[100dvh] bg-gradient-to-br from-slate-50 to-slate-100 flex flex-col lg:flex-row lg:items-center lg:justify-center">
      {showCommunicationConsent && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-lg rounded-2xl bg-white shadow-2xl border border-slate-200 overflow-hidden">
            <div className="p-6">
              <Typography as="h3" className="text-lg font-bold text-primary-900 text-left">
                Consent for SMS/WhatsApp
              </Typography>
              <Typography as="p" className="text-sm text-slate-700 mt-3 text-left leading-relaxed">
                {communicationConsentText}
              </Typography>
            </div>
            <div className="flex items-center justify-end gap-3 px-6 py-4 bg-slate-50 border-t border-slate-200">
              {communicationConsentPending ? (
                <>
                  <Button type="button" variant="secondary" onClick={cancelCommunicationConsent}>
                    Cancel
                  </Button>
                  <Button type="button" onClick={acceptCommunicationConsent}>
                    I Consent
                  </Button>
                </>
              ) : (
                <Button type="button" onClick={cancelCommunicationConsent}>
                  Close
                </Button>
              )}
            </div>
          </div>
        </div>
      )}

      {showCodeOfConduct && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-2xl rounded-2xl bg-white shadow-2xl border border-slate-200 overflow-hidden">
            <div className="p-6">
              <Typography as="h3" className="text-lg font-bold text-primary-900 text-left">
                Code of Conduct and Non-Profit ethics
              </Typography>
              <div className="mt-4 max-h-[60vh] overflow-y-auto rounded-lg border border-slate-200 bg-slate-50 p-4 text-left">
                <pre className="whitespace-pre-wrap text-sm text-slate-800 font-sans leading-relaxed m-0">
                  {codeOfConductPopupContent}
                </pre>
              </div>
            </div>
            <div className="flex items-center justify-end gap-3 px-6 py-4 bg-slate-50 border-t border-slate-200">
              <Button type="button" onClick={() => {
                setHasViewedCodeOfConduct(true);
                setShowCodeOfConduct(false);
              }}>
                Close
              </Button>
              <Button type="button" onClick={() => {
                handleInputChange("code_of_conduct", true);
                setHasViewedCodeOfConduct(true);
                setShowCodeOfConduct(false);
              }}>
                I Agree
              </Button>
            </div>
          </div>
        </div>
      )}
      {selectedRole === "mentor" || selectedRole === "student" ? (
        // Mentor layout: Split layout with form on left and image on right. Left side is one scroll so button is reachable on small screens.
        <div className="w-full max-w-7xl flex-1 lg:flex-none grid grid-cols-1 lg:grid-cols-2 bg-white shadow-xl rounded-2xl border border-slate-200 overflow-hidden min-h-[100dvh] lg:min-h-0 lg:h-[100dvh] lg:max-h-[100dvh]">
          {/* Left side: Form - single scrollable column so submit button and footer are reachable at any zoom */}
          <div
            className="flex flex-col p-6 md:p-8 pb-28 md:pb-32 gap-4 overflow-y-auto overflow-x-hidden min-h-0 lg:h-full lg:max-h-[100dvh]"
          >
            {/* Logo */}
            <div className="flex justify-start">
              <img src="/ign-logo.svg" alt="IgniteNext Logo" className="h-12 w-auto" />
            </div>
            
            {/* Dropdown (only when role is not locked via URL) */}
            {!isRoleLocked && (
              <div className="relative w-full max-w-xs dropdown-container">
                <button
                  type="button"
                  onClick={() => setDropdownOpen(!dropdownOpen)}
                  className="w-full flex items-center justify-between gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2.5 text-sm text-slate-900 shadow-sm hover:border-brand-blue focus:border-brand-blue focus:outline-none focus:ring-2 focus:ring-brand-blue transition-colors"
                >
                  <span>{roleDisplayName(selectedRole) || selectedRole}</span>
                  <Icon
                    icon="lucide:chevron-down"
                    className={`h-4 w-4 text-slate-400 transition-transform ${dropdownOpen ? 'rotate-180' : ''}`}
                  />
                </button>
                {dropdownOpen && (
                  <div className="absolute z-10 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg overflow-hidden">
                    <div className="py-1">
                      <button
                        type="button"
                        onClick={() => {
                          setSelectedRole("volunteer");
                          setDropdownOpen(false);
                        }}
                        className={`w-full text-left px-4 py-2 text-sm transition-colors ${
                          selectedRole === "volunteer"
                            ? "bg-brand-blue text-white"
                            : "text-slate-700 hover:bg-slate-50"
                        }`}
                      >
                        Volunteer
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setSelectedRole("mentor");
                          setDropdownOpen(false);
                        }}
                        className={`w-full text-left px-4 py-2 text-sm transition-colors ${
                          selectedRole === "mentor"
                            ? "bg-brand-blue text-white"
                            : "text-slate-700 hover:bg-slate-50"
                        }`}
                      >
                        Mentor
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setSelectedRole("student");
                          setDropdownOpen(false);
                        }}
                        className={`w-full text-left px-4 py-2 text-sm transition-colors ${
                          selectedRole === "student"
                            ? "bg-brand-blue text-white"
                            : "text-slate-700 hover:bg-slate-50"
                        }`}
                      >
                        Student
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setSelectedRole("ecosystem_partner");
                          setFormData(getPartnerInvestorFormData());
                          setPartnerInvestorStage("form");
                          setPartnerInvestorOtpCode("");
                          setCurrentStep(0);
                          setDropdownOpen(false);
                        }}
                        className={`w-full text-left px-4 py-2 text-sm transition-colors ${
                          selectedRole === "ecosystem_partner"
                            ? "bg-brand-blue text-white"
                            : "text-slate-700 hover:bg-slate-50"
                        }`}
                      >
                        Ecosystem Partner
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setSelectedRole("corporate_sponsor");
                          setFormData(getPartnerInvestorFormData());
                          setPartnerInvestorStage("form");
                          setPartnerInvestorOtpCode("");
                          setCurrentStep(0);
                          setDropdownOpen(false);
                        }}
                        className={`w-full text-left px-4 py-2 text-sm transition-colors ${
                          selectedRole === "corporate_sponsor"
                            ? "bg-brand-blue text-white"
                            : "text-slate-700 hover:bg-slate-50"
                        }`}
                      >
                        Corporate Sponsor
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setSelectedRole("investor");
                          setFormData(getPartnerInvestorFormData());
                          setPartnerInvestorStage("form");
                          setPartnerInvestorOtpCode("");
                          setCurrentStep(0);
                          setDropdownOpen(false);
                        }}
                        className={`w-full text-left px-4 py-2 text-sm transition-colors ${
                          selectedRole === "investor"
                            ? "bg-brand-blue text-white"
                            : "text-slate-700 hover:bg-slate-50"
                        }`}
                      >
                        Investor
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Title */}
            <div className="mt-4">
              <Typography as="h1" className="text-3xl font-bold text-primary-900 text-left">
                {mentorStage === "otp"
                  ? "Verify your email"
                  : mentorStage === "thanks"
                    ? "Thank you!"
                    : selectedRole === "student"
                      ? "Start Your Innovation Journey"
                      : "Be the Force Multiplier"}
              </Typography>
              <Typography as="p" className="text-sm text-tertiary-600 mt-2 text-left">
                {mentorStage === "otp"
                  ? `Enter the 6-digit code sent to ${mentorOtpEmail || formData.email || "your email"}.`
                  : mentorStage === "thanks"
                    ? `OTP verified successfully. Your ${roleDisplayName(selectedRole) || selectedRole} account has been created.`
                    : selectedRole === "student"
                      ? "Join IgniteNext and turn your ideas into reality"
                      : "Your expertise is the bridge between a student's \"Spark\" and a market-ready venture"}
              </Typography>
            </div>

            {mentorStage === "thanks" ? (
              <div className="flex flex-col gap-4">
                <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
                  You’re all set. You can now log in and get started.
                </div>
                <Button type="button" className="w-full" onClick={() => navigate("/login")}>
                  Go to login
                </Button>
              </div>
            ) : (
              /* Form - content and button in one flow; entire left column scrolls so button is always reachable */
              <form className="flex flex-col gap-4" onSubmit={handleSubmit}>
                <div className="flex flex-col gap-4 pb-2">
                  {renderStepContent()}
                </div>

                <div className="min-h-[52px] pt-3 pb-1 space-y-3 border-t border-slate-100 bg-white">
                  {error && (
                    <div className="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-600">
                      <Icon icon="lucide:alert-circle" className="h-4 w-4" />
                      <span>{error}</span>
                    </div>
                  )}

                  <Button
                    type="submit"
                    className="w-full"
                    // Scoped to mentorStage === "form": this same button becomes the
                    // "Verify code" action during the email-OTP step, where the phone
                    // gate must not apply or that step becomes unclickable.
                    disabled={
                      submitting ||
                      !!emailExistsError ||
                      !!usernameExistsError ||
                      (mentorStage === "form" && !phoneVerified)
                    }
                  >
                    {mentorStage === "otp"
                      ? submitting
                        ? "Verifying..."
                        : "Verify code"
                      : submitting
                        ? (selectedRole === "student" ? "Igniting Your Idea..." : "Joining the Mission...")
                        : (selectedRole === "student" ? "Ignite Your Idea" : "Join the Mission")}
                  </Button>
                </div>
              </form>
            )}

            {/* Login Link */}
            <div className="text-sm text-tertiary-600 text-center shrink-0">
              Already have an account?{" "}
              <Link to="/login" className="text-brand-blue hover:underline">
                Log in
              </Link>
            </div>

            {/* Footer - extra padding so full bottom is scrollable into view at any zoom (110%, 125%, etc.) */}
            <div className="text-xs text-tertiary-600 text-center shrink-0 pt-4 pb-6">
              <div>© IgniteNext {new Date().getFullYear()}</div>
              <div>contact@ignitenext.org</div>
            </div>
          </div>

          {/* Right side: Image */}
          <div className="hidden lg:block h-full min-h-0">
            <img src={mentorSignUpImage} alt={selectedRole === "student" ? "Student Sign Up" : "Mentor Sign Up"} className="w-full h-full object-cover" />
          </div>
        </div>
      ) : (
        // Volunteer layout: Split layout with form on left and image on right
        <div className="w-full max-w-7xl flex-1 lg:flex-none grid grid-cols-1 lg:grid-cols-2 bg-white shadow-xl rounded-2xl border border-slate-200 overflow-hidden min-h-[100dvh] lg:h-[100dvh] lg:max-h-[100dvh]">
          {/* Left side: Form - scrollable to bottom at any zoom */}
          <div className="flex flex-col p-8 md:p-10 pb-28 md:pb-32 gap-6 overflow-y-auto overflow-x-hidden min-h-0 h-full max-h-[100dvh]">
            {/* Logo */}
            <div className="flex justify-start">
              <img src="/ign-logo.svg" alt="IgniteNext Logo" className="h-12 w-auto" />
            </div>
            
            {/* Dropdown (only when role is not locked via URL) */}
            {!isRoleLocked && (
              <div className="relative w-full max-w-xs dropdown-container">
                <button
                  type="button"
                  onClick={() => setDropdownOpen(!dropdownOpen)}
                  className="w-full flex items-center justify-between gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2.5 text-sm text-slate-900 shadow-sm hover:border-brand-blue focus:border-brand-blue focus:outline-none focus:ring-2 focus:ring-brand-blue transition-colors"
                >
                  <span>{roleDisplayName(selectedRole) || selectedRole}</span>
                  <Icon
                    icon="lucide:chevron-down"
                    className={`h-4 w-4 text-slate-400 transition-transform ${dropdownOpen ? 'rotate-180' : ''}`}
                  />
                </button>
                {dropdownOpen && (
                  <div className="absolute z-10 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg overflow-hidden">
                    <div className="py-1">
                      <button
                        type="button"
                        onClick={() => {
                          setSelectedRole("volunteer");
                          setDropdownOpen(false);
                        }}
                        className={`w-full text-left px-4 py-2 text-sm transition-colors ${
                          selectedRole === "volunteer"
                            ? "bg-brand-blue text-white"
                            : "text-slate-700 hover:bg-slate-50"
                        }`}
                      >
                        Volunteer
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setSelectedRole("mentor");
                          setDropdownOpen(false);
                        }}
                        className={`w-full text-left px-4 py-2 text-sm transition-colors ${
                          selectedRole === "mentor"
                            ? "bg-brand-blue text-white"
                            : "text-slate-700 hover:bg-slate-50"
                        }`}
                      >
                        Mentor
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setSelectedRole("student");
                          setDropdownOpen(false);
                        }}
                        className={`w-full text-left px-4 py-2 text-sm transition-colors ${
                          selectedRole === "student"
                            ? "bg-brand-blue text-white"
                            : "text-slate-700 hover:bg-slate-50"
                        }`}
                      >
                        Student
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setSelectedRole("ecosystem_partner");
                          setFormData(getPartnerInvestorFormData());
                          setPartnerInvestorStage("form");
                          setPartnerInvestorOtpCode("");
                          setCurrentStep(0);
                          setDropdownOpen(false);
                        }}
                        className={`w-full text-left px-4 py-2 text-sm transition-colors ${
                          selectedRole === "ecosystem_partner"
                            ? "bg-brand-blue text-white"
                            : "text-slate-700 hover:bg-slate-50"
                        }`}
                      >
                        Ecosystem Partner
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setSelectedRole("corporate_sponsor");
                          setFormData(getPartnerInvestorFormData());
                          setPartnerInvestorStage("form");
                          setPartnerInvestorOtpCode("");
                          setCurrentStep(0);
                          setDropdownOpen(false);
                        }}
                        className={`w-full text-left px-4 py-2 text-sm transition-colors ${
                          selectedRole === "corporate_sponsor"
                            ? "bg-brand-blue text-white"
                            : "text-slate-700 hover:bg-slate-50"
                        }`}
                      >
                        Corporate Sponsor
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setSelectedRole("investor");
                          setFormData(getPartnerInvestorFormData());
                          setPartnerInvestorStage("form");
                          setPartnerInvestorOtpCode("");
                          setCurrentStep(0);
                          setDropdownOpen(false);
                        }}
                        className={`w-full text-left px-4 py-2 text-sm transition-colors ${
                          selectedRole === "investor"
                            ? "bg-brand-blue text-white"
                            : "text-slate-700 hover:bg-slate-50"
                        }`}
                      >
                        Investor
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Title and Step Info (hidden on volunteer/user thanks; confirmation card shown instead) */}
            {((selectedRole === "volunteer" && volunteerStage !== "thanks") || (selectedRole === "user" && userStage !== "thanks") || isPartnerOrInvestorRole(selectedRole)) && (
            <div className="mt-4">
              <Typography as="h1" className="text-3xl font-bold text-primary-900 text-left">
                {isPartnerOrInvestorRole(selectedRole)
                  ? (currentStep === 1 && partnerInvestorStage === "otp"
                      ? "Verify your email"
                      : selectedRole === "ecosystem_partner"
                        ? "Architecting Change, Together"
                        : selectedRole === "corporate_sponsor"
                          ? "Fueling Innovation, Scaling Impact"
                          : "Investor registration")
                  : selectedRole === "user"
                    ? (userEventName || events.find((e) => e.id === parseInt(formData.event_id, 10))?.title
                      ? `Join ${userEventName || events.find((e) => e.id === parseInt(formData.event_id, 10))?.title} — Powered by IgniteNext`
                      : "Join this event — Powered by IgniteNext")
                    : "Join the IgniteNext Ecosystem – Volunteer Application"}
              </Typography>
              {selectedRole === "volunteer" && (
                <Typography as="p" className="text-sm text-tertiary-600 mt-2 text-left">
                  Thank you for your interest. You aren't just giving your time, you are providing the critical "oxygen" to the platform.
                </Typography>
              )}
              {selectedRole === "user" && (
                <Typography as="p" className="text-sm text-tertiary-600 mt-2 text-left">
                  Part of the IgniteNext global movement — where innovators, leaders, investors, and institutions collaborate to solve real-world problems at scale.
                </Typography>
              )}
              {isPartnerOrInvestorRole(selectedRole) &&
                (selectedRole === "investor" && currentStep === 0 && partnerInvestorStage !== "otp" ? (
                  <div className="mt-2 text-left space-y-1">
                    <Typography as="p" className="text-base font-semibold text-primary-900">
                      Access Premier Deal Flow
                    </Typography>
                    <Typography as="p" className="text-sm text-tertiary-600">
                      Gain early access to high-potential student-led ventures to our curated Pitch Days
                    </Typography>
                  </div>
                ) : (
                  <Typography as="p" className="text-sm text-tertiary-600 mt-2 text-left">
                    {currentStep === 1 && partnerInvestorStage === "otp"
                      ? `Enter the 6-digit code sent to ${partnerInvestorOtpEmail || formData.contact_email || "your email"}.`
                      : selectedRole === "ecosystem_partner"
                        ? "We are honored to welcome mission-aligned partners"
                        : selectedRole === "corporate_sponsor"
                          ? currentStep === 0
                            ? "Join as a strategic catalyst. Your investment drives the journey; your brand defines the legacy."
                            : "Tell us about your organization and primary contact."
                          : "Tell us about your firm and primary representative."}
                  </Typography>
                ))}
              {!isPartnerOrInvestorRole(selectedRole) && (
                <Typography as="p" className="text-sm text-tertiary-600 mt-2 text-left">
                  {`Step ${currentStep + 1} of ${totalSteps}: ${stepTitles[currentStep]}`}
                </Typography>
              )}
          </div>
            )}

          {(selectedRole === "volunteer" && volunteerStage === "thanks") || (selectedRole === "user" && userStage === "thanks") ? (
            selectedRole === "user" ? (
              /* User (unregistered participant) confirmation */
              <div className="mt-2 flex flex-col flex-1 min-h-0">
                <div className="rounded-2xl border border-slate-200 bg-white shadow-xl overflow-hidden flex flex-col max-w-xl mx-auto w-full">
                  <div className="rounded-t-2xl bg-slate-800 text-white text-center px-6 py-4">
                    <Typography as="p" className="text-sm font-semibold tracking-wide">
                      Welcome to the IgniteNext network.
                    </Typography>
                  </div>
                  <div className="p-6 md:p-8 flex flex-col gap-6 flex-1">
                    <div className="flex justify-center">
                      <div className="flex items-center justify-center w-20 h-20 rounded-full bg-emerald-400 text-white shadow-inner">
                        <Icon icon="lucide:check" className="w-10 h-10" strokeWidth={3} />
                      </div>
                    </div>
                    <div className="text-center space-y-2">
                      <Typography as="h2" className="text-2xl md:text-3xl font-bold text-slate-900">
                        Thank you for your interest. You're in.
                      </Typography>
                      <Typography as="p" className="text-slate-600">
                        Thank you for your interest in {userEventName || events.find((e) => e.id === parseInt(formData.event_id, 10))?.title || "this event"}. Our team reviews all submissions to ensure meaningful alignment and impact. You will hear from us shortly with next steps.
                      </Typography>
                      <Typography as="p" className="text-slate-600 font-medium">
                        Welcome to the IgniteNext network.
                      </Typography>
                    </div>
                    <div className="text-center pt-2 border-t border-slate-100">
                      <Typography as="p" className="text-sm text-slate-600">
                        Questions?{" "}
                        <a href="mailto:contact@ignitenext.org" className="text-brand-blue font-medium hover:underline">
                          Contact@ignitenext.org
                        </a>
                      </Typography>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
            /* Volunteer confirmation: "Application in the Vault" design (no login CTA – volunteers don't have login) */
            <div className="mt-2 flex flex-col flex-1 min-h-0">
              <div className="rounded-2xl border border-slate-200 bg-white shadow-xl overflow-hidden flex flex-col max-w-xl mx-auto w-full">
                {/* Mission banner – dark blue-grey */}
                <div className="rounded-t-2xl bg-slate-800 text-white text-center px-6 py-4">
                  <Typography as="p" className="text-sm font-semibold tracking-wide">
                    Together, we are building a world-class innovation ecosystem.
                  </Typography>
                </div>
                <div className="p-6 md:p-8 flex flex-col gap-6 flex-1">
                  {/* Success: light green circle, white checkmark */}
                  <div className="flex justify-center">
                    <div className="flex items-center justify-center w-20 h-20 rounded-full bg-emerald-400 text-white shadow-inner">
                      <Icon icon="lucide:check" className="w-10 h-10" strokeWidth={3} />
                    </div>
                  </div>
                  {/* Main heading and thank you */}
                  <div className="text-center space-y-2">
                    <Typography as="h2" className="text-2xl md:text-3xl font-bold text-slate-900">
                      Your Application is in the Vault.
                    </Typography>
                    <Typography as="p" className="text-slate-600">
                      Thank you for committing to the IgniteNext mission!
                    </Typography>
                  </div>
                  {/* What happens next */}
                  <div className="space-y-3">
                    <Typography as="h3" className="text-sm font-bold uppercase tracking-wider text-slate-900">
                      WHAT HAPPENS NEXT?
                    </Typography>
                    <Typography as="p" className="text-sm text-slate-600 leading-relaxed">
                      To maintain high standards and ensure safety, our team is validating your application for professional alignment and commitment to our Code of Conduct.
                    </Typography>
                  </div>
                  {/* Timeline & Validation cards */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="rounded-xl bg-indigo-50/80 border border-indigo-100 px-4 py-4">
                      <Typography as="p" className="text-sm font-bold text-blue-700 mb-1">
                        Timeline
                      </Typography>
                      <Typography as="p" className="text-sm text-slate-700">
                        Update within 3–5 business days.
                      </Typography>
                    </div>
                    <div className="rounded-xl bg-violet-50/90 border border-violet-200 px-4 py-4">
                      <Typography as="p" className="text-sm font-bold text-violet-700 mb-1">
                        Validation
                      </Typography>
                      <Typography as="p" className="text-sm text-slate-700">
                        Watch for your &ldquo;Welcome to IgniteNext&rdquo; email.
                      </Typography>
                    </div>
                  </div>
                  {/* Contact */}
                  <div className="text-center pt-2 border-t border-slate-100">
                    <Typography as="p" className="text-sm text-slate-600">
                      Questions?{" "}
                      <a
                        href="mailto:contact@ignitenext.org"
                        className="text-brand-blue font-medium hover:underline"
                      >
                        Contact@ignitenext.org
                      </a>
                    </Typography>
                  </div>
                </div>
              </div>
            </div>
            )
          ) : (
            <>
              {/* Progress Bar */}
              <div className="w-full mb-6">
                {isPartnerOrInvestorRole(selectedRole) ? (
                  <>
                    <div className="flex items-center justify-between mb-2">
                      <Typography as="span" className="text-sm font-medium text-primary-900">
                        {stepTitles[currentStep]}
                      </Typography>
                      <Typography as="span" className="text-sm text-tertiary-600">
                        Step {currentStep + 1} of {totalSteps}
                      </Typography>
                    </div>
                    {selectedRole === "corporate_sponsor" && currentStep === 0 && (
                      <Typography as="p" className="text-sm text-tertiary-600 text-left mb-2">
                        Investment Profile & Brand Identity
                      </Typography>
                    )}
                    <div className="w-full h-1 bg-slate-200 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-[#7F56D9] transition-all duration-300 ease-in-out"
                        style={{ width: `${((currentStep + 1) / totalSteps) * 100}%` }}
                      />
                    </div>
                  </>
                ) : (
                  <>
                    <div className="flex items-center justify-between mb-2">
                      {stepTitles.map((title, index) => (
                        <div
                          key={index}
                          className={`flex-1 h-2 mx-1 rounded-full ${
                            index <= currentStep ? "bg-brand-blue" : "bg-slate-200"
                          }`}
                        />
                      ))}
                    </div>
                    <div className="text-xs text-left text-tertiary-600 mt-1">
                      {stepTitles[currentStep]}
                    </div>
                  </>
                )}
              </div>

              {/* Form */}
              <form className="flex flex-col gap-4" onSubmit={handleSubmit}>
                {renderStepContent()}

                {error && (
                  <div className="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-600">
                    <Icon icon="lucide:alert-circle" className="h-4 w-4" />
                    <span>{error}</span>
                  </div>
                )}

                <div className="flex flex-row gap-4 items-center">
                  {currentStep > 0 && (
                    <Button
                      type="button"
                      onClick={handleBack}
                      variant="secondary"
                      className="flex-1"
                      startIcon={<Icon icon="lucide:arrow-left" className="h-4 w-4" />}
                    >
                      Back
                    </Button>
                  )}
                  {currentStep < totalSteps - 1 ? (
                    <Button
                      type="button"
                      onClick={selectedRole === "user" && currentStep === 0 ? handleUserStep0Next : handleNext}
                      className="flex-1"
                      disabled={selectedRole === "user" && currentStep === 0 && (submitting || !!userExistsMessage)}
                      endIcon={<Icon icon="lucide:arrow-right" className="h-4 w-4" />}
                    >
                      {selectedRole === "user" && currentStep === 0 && submitting
                        ? "Checking..."
                        : selectedRole === "corporate_sponsor" && currentStep === 0
                          ? "Next to Define Brand Visibility ->"
                          : "Next"}
                    </Button>
                  ) : (
                    <Button
                      type="submit"
                      fullWidth={currentStep === 0}
                      className={currentStep > 0 ? "flex-1" : ""}
                      // Partner/investor keep their phone on the final step, so the
                      // gate belongs here — scoped to the "form" stage because this
                      // button doubles as "Verify code" during the email OTP.
                      // Volunteer's phone sits on step 1 and is gated by
                      // validateStep(1) via handleNext, well before this button.
                      disabled={
                        submitting ||
                        !!emailExistsError ||
                        !!usernameExistsError ||
                        (isPartnerOrInvestorRole(selectedRole) &&
                          partnerInvestorStage === "form" &&
                          !phoneVerified)
                      }
                    >
                      {submitting 
                        ? (selectedRole === "volunteer" ? "Submitting your application..." : isPartnerOrInvestorRole(selectedRole) ? "Submitting..." : "Creating account...")
                        : (selectedRole === "volunteer" ? "Submit Your Application" : isPartnerOrInvestorRole(selectedRole) ? (currentStep === totalSteps - 1 ? "Submit" : "Next") : "Create account")
                      }
                    </Button>
                  )}
                </div>
              </form>
            </>
          )}

            {/* Login CTA for investor, ecosystem_partner, corporate_sponsor (same as mentor/student signup) */}
            {isPartnerOrInvestorRole(selectedRole) && (
              <div className="text-sm text-tertiary-600 text-center shrink-0">
                Already have an account?{" "}
                <Link to="/login" className="text-brand-blue hover:underline">
                  Log in
                </Link>
              </div>
            )}

            {/* Footer - extra padding so full bottom is scrollable at any zoom */}
            <div className="text-xs text-tertiary-600 text-center mt-auto pt-4 pb-6">
              <div>© IgniteNext {new Date().getFullYear()}</div>
              <div>contact@ignitenext.org</div>
            </div>
          </div>

          {/* Right side: Image */}
          <div className="hidden lg:block h-full min-h-0">
            <img src={mentorSignUpImage} alt="Volunteer Sign Up" className="w-full h-full object-cover" />
          </div>
        </div>
      )}
    </div>
  );
};

export default SignUp;

